/**
 * NNADD Configurador — a página do configurador
 * (blocks/nnadd-configurador.liquid, plano U7).
 *
 * O cliente escolhe a peça, a cor, o tamanho, o lugar (ou os dois lugares de
 * "as duas terras"), a composição, o estilo de mapa, a frase, o estampado e o
 * QR, e vê a peça ao vivo. Só consegue encomendar uma combinação válida: o
 * botão de compra segue pela validação nativa do formulário, com
 * `setCustomValidity()` no campo do problema ou na checkbox de confirmação.
 *
 * As decisões estão em assets/nnadd-configurador-estado.js (testado no Node);
 * este ficheiro só liga o estado ao DOM. Sem imports estáticos: os módulos
 * chegam por `import()` com os URLs do CDN que o Liquid põe em `data-*`:
 * - no arranque: o desenho (U5), os glifos (U2), os mapas (U3), `formatarDMS`
 *   da Coordinates e o estado (o Liquid põe-nos em `modulepreload`);
 * - na primeira pesquisa (ou com `?lugar=` no URL): a pesquisa no Nominatim;
 * - só quando o cliente escolhe uma composição com mapa: o carregador de
 *   dados (U4) e os descodificadores de tiles.
 * O Leaflet 1.9.4 vem do cdnjs (por assets/nnadd-leaflet.js), e só quando o
 * cliente abre "Ajustar no mapa".
 *
 * Variante (KTD4): peça, cor e tamanho escolhem-se nos rádios deste bloco, mas
 * quem muda a variante é o seletor do tema (escondido quando o bloco está
 * pronto). `sincronizarVariante` marca os rádios do seletor; as mudanças
 * voltam pelo evento `shopify:product:select`, como em
 * assets/nnadd-coordinates.js.
 *
 * Rascunho (KTD15): as escolhas ficam em `sessionStorage`, para a troca de
 * língua as repor.
 *
 * Disposição (plano de 2026-10-22, U4): os rádios `disposicoes` (Liquid)
 * mudam a disposição e pedem logo a variante "+ costas" (ou sem costas) ao
 * seletor do tema; o extra das costas vem de `data-extra-costas` (cêntimos da
 * moeda servida) e escreve-se no formato da língua. O estilo das costas só
 * aparece em frente + costas. A pré-visualização plana mostra a frente e,
 * nas disposições com costas, as costas ao lado (`svgDaPrevia`).
 *
 * Telemóvel (auditoria de 2026-10-22): a figura da pré-visualização sai do
 * fluxo (CSS) e ao topo fica presa só a faixa com o recorte do desenho
 * (`svgDoRecorte`) e o preço; tocar nela leva a figura para um `<dialog>` de
 * ecrã inteiro e fechá-lo devolve-a. Em baixo, a barra de compra própria
 * mostra o preço da variante que a disposição pede (lido de `data-produto`,
 * sem esperar pelo tema), a razão do bloqueio e um botão que carrega no botão
 * do tema; depois de adicionar, abre a gaveta do carrinho do tema.
 */

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ZOOM_INICIAL = 14;
const PASSO_PX = 8;
const PASSO_PX_GRANDE = 48;

const RASCUNHO_PREFIXO = 'nnadd-configurador:rascunho:v1:';


/** O evento do tema quando uma linha entra no carrinho (`StandardEvents.cartLinesUpdate` de @shopify/events). */
const EVENTO_CARRINHO = 'shopify:cart:lines-update';
/** Quanto tempo o botão da barra diz "Adicionada". */
const ADICIONADA_MS = 2500;
/** Os recortes da faixa: a margem à volta do desenho e a proporção máxima (largura/altura) do retângulo. */
const RECORTE_MARGEM = 0.08;
const RECORTE_PROPORCAO_MAX = 2.4;

/**
 * As larguras (mm) das linhas de coordenadas a tracejado do marcador, por
 * composição. Em "as duas terras" as coordenadas ficam dentro da moldura.
 */
const LINHAS_MARCADOR = Object.freeze({
  coordenadas: Object.freeze([90, 90]),
  coordenadas_mapa: Object.freeze([110]),
  duas_terras: Object.freeze([]),
});


/** As ações que não vêm do cliente: só mudam os avisos se trouxerem avisos novos. */
const ACOES_DO_SISTEMA = new Set(['variante', 'mapa_a_carregar', 'mapa_carregado', 'confirmar', 'historia', 'publicar']);

/** O id numérico de uma variante, venha como número, texto ou gid. */
function idVariante(/** @type {unknown} */ id) {
  return /(\d+)$/.exec(String(id ?? ''))?.[1] ?? '';
}

const n = (/** @type {number} */ v) => String(Number(v.toFixed(3)));

/** @param {string} texto */
function escaparXml(texto) {
  return String(texto).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * O `<svg>` em mm de um marcador, com pelo menos 10 mm de altura.
 * @param {number} largura @param {number} altura @param {string} corpo
 */
function svgMarcador(largura, altura, corpo) {
  const H = Math.max(altura, 10);
  return `<svg xmlns="${SVG_NS}" viewBox="0 0 ${n(largura)} ${n(H)}" width="${n(largura)}mm" height="${n(H)}mm">${corpo}</svg>`;
}

/** @param {string} modelo @param {Record<string, string | number>} valores */
function preencher(modelo, valores) {
  return Object.entries(valores).reduce((t, [k, v]) => t.split(`[${k}]`).join(String(v)), String(modelo ?? ''));
}

/* ---------------------------------------------------------------------------
 * Leaflet: o carregador partilhado (assets/nnadd-leaflet.js), importado do URL
 * em `data-leaflet` só quando o cliente abre "Ajustar no mapa".
 * ------------------------------------------------------------------------- */

/** @param {string | undefined} url */
async function carregarLeaflet(url) {
  if (!url) throw new Error('Falta data-leaflet');
  const m = await import(url);
  return m.carregarLeaflet();
}

/* ---------------------------------------------------------------------------
 * Pré-visualização: as silhuetas planas das peças (cromado da interface, não
 * é o produto) e o desenho do módulo U5 por cima. Nas disposições com costas,
 * a peça de frente à esquerda e a de costas à direita.
 * ------------------------------------------------------------------------- */

const SVG_NS = 'http://www.w3.org/2000/svg';
const LARGURA_PECA = 400;
const TOPO_PECA = -14;
const ALTURA_PECA = 450;
// Um peito de cerca de 62 cm ocupa 264 unidades (como na Coordinates).
const UNIDADES_POR_MM = 264 / 620;
// O topo do desenho, a meio do peito: a frente fica a FRENTE_TOPO_POL do topo da área (o mesmo valor
// de tools/coordinates/src/configurador.js e de TOPO_POL em assets/nnadd-configurador-fotos.js).
const DESENHO_TOPO = 112;
const FRENTE_TOPO_POL = 3;
const MM_POR_POL = 25.4;
// Duas peças: o espaço entre elas e a faixa das legendas por baixo.
const ESPACO_LADOS = 40;
const LEGENDA_ALTURA = 36;
const PORMENOR_TOPO = TOPO_PECA + ALTURA_PECA + 16;
const PORMENOR_LARGURA = 300;
const PORMENOR_ALTURA_MAX = 300;
const PORMENOR_MARGEM = 28;

/**
 * O corpo é o mesmo dos dois lados; as costas não têm bolso, cordões nem a gola da frente, e na hoodie
 * o capuz cai pelas costas.
 * @type {Record<string, { corpo: string, detalhes: string[], detalhesCostas: string[] }>}
 */
const SILHUETAS = {
  tshirt: {
    corpo: 'M140 38 Q200 78 260 38 L334 58 L396 152 L344 182 L330 162 L334 430 L66 430 L70 162 L56 182 L4 152 L66 58 Z',
    detalhes: ['M140 38 Q200 78 260 38 Q200 92 140 38 Z'],
    detalhesCostas: ['M140 38 Q200 54 260 38 Q200 66 140 38 Z'],
  },
  sweatshirt: {
    corpo:
      'M140 40 Q200 74 260 40 L330 58 Q362 70 372 112 L398 384 L350 392 L332 196 L334 420 Q200 430 66 420 L68 196 L50 392 L2 384 L28 112 Q38 70 70 58 Z',
    detalhes: ['M140 40 Q200 74 260 40 Q200 86 140 40 Z', 'M68 404 Q200 414 332 404', 'M8 368 L54 374', 'M392 368 L346 374'],
    detalhesCostas: ['M140 40 Q200 56 260 40 Q200 66 140 40 Z', 'M68 404 Q200 414 332 404', 'M8 368 L54 374', 'M392 368 L346 374'],
  },
  hoodie: {
    corpo:
      'M124 48 Q126 -6 200 -8 Q274 -6 276 48 L330 60 Q362 72 372 114 L398 384 L350 392 L332 196 L334 420 Q200 430 66 420 L68 196 L50 392 L2 384 L28 114 Q38 72 70 60 Z',
    detalhes: [
      'M146 52 Q200 96 254 52',
      'M186 78 L184 150',
      'M214 78 L216 150',
      'M118 312 L282 312 L304 392 L96 392 Z',
      'M68 404 Q200 414 332 404',
    ],
    detalhesCostas: [
      // O capuz por trás: a forma pousada nos ombros, com a costura do meio.
      'M124 48 Q126 -6 200 -8 Q274 -6 276 48 Q266 88 200 96 Q134 88 124 48 Z',
      'M200 -8 L200 96',
      'M68 404 Q200 414 332 404',
    ],
  },
};

/**
 * Um desenho do módulo U5 (SVG em mm, texto) como `<svg>` aninhado: centrado em `largura` a partir de
 * `x`, com o topo em `y`, e encolhido se passar de `alturaMax`.
 * @param {string} svg @param {number} x @param {number} y @param {number} largura @param {number} [alturaMax]
 */
function desenhoPosicionado(svg, x, y, largura, alturaMax = Infinity) {
  const m = /^\s*<svg\b([^>]*)>([\s\S]*)<\/svg>\s*$/.exec(svg);
  const viewBox = /viewBox="([^"]+)"/.exec(m?.[1] ?? '')?.[1] ?? '0 0 1 1';
  const [, , vbL, vbA] = viewBox.trim().split(/[\s,]+/).map(Number);
  let w = largura;
  let h = (largura * vbA) / vbL;
  if (h > alturaMax) {
    h = alturaMax;
    w = (alturaMax * vbL) / vbA;
  }
  return {
    markup: `<svg x="${n(x + (largura - w) / 2)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" viewBox="${viewBox}" aria-hidden="true">${m?.[2] ?? ''}</svg>`,
    altura: h,
  };
}

/**
 * O SVG da pré-visualização plana (texto, sem DOM). Só frente: a peça com o desenho no peito, como
 * sempre. Com costas: a frente à esquerda e as costas à direita, cada uma com o seu desenho e a sua
 * legenda; as costas começam à mesma costura da frente, a `topoCostasPol` em vez de FRENTE_TOPO_POL
 * (a hoodie, a 2,5 in, fica um pouco mais acima). Por baixo, o pormenor ampliado de `pormenor`, o
 * lado com o desenho principal.
 * @param {{ peca: string, tom?: string, hex?: string, frente: string, costas?: string | null,
 *   topoCostasPol?: number, pormenor?: 'frente' | 'costas', idTitulo: string, titulo: string,
 *   legendas?: { frente?: string, costas?: string } }} p
 */
export function svgDaPrevia({ peca, tom = 'escura', hex = '#cccccc', frente, costas = null, topoCostasPol = FRENTE_TOPO_POL, pormenor = 'frente', idTitulo, titulo, legendas = {} }) {
  const silhueta = SILHUETAS[peca] ?? SILHUETAS.tshirt;
  const largura = (/** @type {string} */ desenho) => Number(/viewBox="0 0 ([\d.]+) /.exec(desenho)?.[1] ?? 120) * UNIDADES_POR_MM;
  const lados = costas ? 2 : 1;
  const larguraTotal = lados * LARGURA_PECA + (lados - 1) * ESPACO_LADOS;
  const legenda = costas ? LEGENDA_ALTURA : 0;

  /** Uma peça, de frente ou de costas, com o desenho do lado, a partir de `x0`. */
  const pecaEm = (/** @type {number} */ x0, /** @type {string} */ desenho, /** @type {boolean} */ deCostas, /** @type {string | undefined} */ texto) => {
    const topo = deCostas ? DESENHO_TOPO + (topoCostasPol - FRENTE_TOPO_POL) * MM_POR_POL * UNIDADES_POR_MM : DESENHO_TOPO;
    const w = largura(desenho);
    const detalhes = deCostas ? silhueta.detalhesCostas : silhueta.detalhes;
    return (
      `<g transform="translate(${x0} 0)">` +
      `<path class="nnadd-conf__peca" d="${silhueta.corpo}"/>` +
      detalhes.map((d) => `<path class="nnadd-conf__peca-detalhe" d="${d}"/>`).join('') +
      desenhoPosicionado(desenho, (LARGURA_PECA - w) / 2, topo, w).markup +
      (costas ? `<text class="nnadd-conf__lado" x="${LARGURA_PECA / 2}" y="${n(TOPO_PECA + ALTURA_PECA + LEGENDA_ALTURA - 10)}" text-anchor="middle">${escaparXml(texto ?? '')}</text>` : '') +
      `</g>`
    );
  };

  const topoPormenor = PORMENOR_TOPO + legenda;
  const larguraPormenor = costas ? PORMENOR_LARGURA * 1.6 : PORMENOR_LARGURA;
  const ampliado = desenhoPosicionado(
    (pormenor === 'costas' && costas) || frente,
    (larguraTotal - larguraPormenor) / 2,
    topoPormenor + PORMENOR_MARGEM,
    larguraPormenor,
    costas ? PORMENOR_ALTURA_MAX * 1.2 : PORMENOR_ALTURA_MAX
  );
  const alturaPormenor = ampliado.altura + PORMENOR_MARGEM * 2;
  const alturaTotal = topoPormenor + alturaPormenor - TOPO_PECA;

  return (
    `<svg xmlns="${SVG_NS}" viewBox="0 ${TOPO_PECA} ${larguraTotal} ${n(alturaTotal)}" class="nnadd-conf__svg" role="img" focusable="false" ` +
    `aria-labelledby="${escaparXml(idTitulo)}" data-tom="${escaparXml(tom)}" data-lados="${lados}" style="--nnadd-conf-peca: ${escaparXml(hex)}">` +
    `<title id="${escaparXml(idTitulo)}">${escaparXml(titulo)}</title>` +
    pecaEm(0, frente, false, legendas.frente) +
    (costas ? pecaEm(LARGURA_PECA + ESPACO_LADOS, costas, true, legendas.costas) : '') +
    `<rect class="nnadd-conf__pormenor" x="0" y="${topoPormenor}" width="${larguraTotal}" height="${n(alturaPormenor)}"/>` +
    ampliado.markup +
    `</svg>`
  );
}

/**
 * O recorte de um lado para a faixa do telemóvel (texto, sem DOM): o desenho sobre o retângulo escuro
 * do pormenor, com uma margem à volta; um desenho muito largo (só coordenadas) leva mais altura, para o
 * retângulo nunca ficar uma tira.
 * @param {string} desenho SVG em mm @param {string} [hex] @param {string} [tom]
 */
export function svgDoRecorte(desenho, hex = '#cccccc', tom = 'escura') {
  const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(desenho);
  const vbL = Number(vb?.[1] ?? 100);
  const vbA = Number(vb?.[2] ?? 100);
  const margem = Math.max(vbL, vbA) * RECORTE_MARGEM;
  const largura = vbL + 2 * margem;
  const altura = Math.max(vbA + 2 * margem, largura / RECORTE_PROPORCAO_MAX);
  const posto = desenhoPosicionado(desenho, margem, (altura - vbA) / 2, vbL);
  return (
    `<svg xmlns="${SVG_NS}" viewBox="0 0 ${n(largura)} ${n(altura)}" class="nnadd-conf__recorte-svg" aria-hidden="true" focusable="false" ` +
    `data-tom="${escaparXml(tom)}" style="--nnadd-conf-peca: ${escaparXml(hex)}">` +
    `<rect class="nnadd-conf__pormenor" x="0" y="0" width="${n(largura)}" height="${n(altura)}"/>` +
    posto.markup +
    `</svg>`
  );
}

/* ---------------------------------------------------------------------------
 * Um lugar: a pesquisa, as coordenadas e o ajuste no mapa (dois em "as duas
 * terras", cada um com o seu estado).
 * ------------------------------------------------------------------------- */

class LugarUI {
  /** @type {Record<string, any>} */ refs = {};
  /** @type {any[]} */ resultados = [];
  /** @type {any} */ pedidos = null;
  /** @type {any} */ mapa = null;
  /** @type {any} */ pin = null;
  aAjustar = false;

  /**
   * @param {NnaddConfiguradorForm} host
   * @param {HTMLElement} el o contentor `[data-lugar]`
   * @param {0 | 1} indice
   */
  constructor(host, el, indice) {
    this.host = host;
    this.el = el;
    this.indice = indice;
    for (const r of el.querySelectorAll('[data-ref]')) this.refs[/** @type {HTMLElement} */ (r).dataset.ref ?? ''] = r;
    const { campo, procurar, lista, abrirMapa, ajustar, concluido, mapa } = this.refs;
    campo.addEventListener('keydown', this.aoTeclarCampo);
    campo.addEventListener('input', this.aoEditarCampo);
    procurar.addEventListener('click', this.pesquisar);
    lista.addEventListener('click', this.aoClicarResultado);
    lista.addEventListener('keydown', this.aoTeclarLista);
    abrirMapa.addEventListener('click', this.alternarMapa);
    ajustar.addEventListener('click', () => this.definirAjuste(true));
    concluido.addEventListener('click', () => this.definirAjuste(false));
    mapa.addEventListener('keydown', this.aoTeclarMapa);
  }

  get textos() {
    return this.host.textos;
  }

  /** O lugar escolhido no estado, ou null. */
  get lugar() {
    return this.host.estado?.lugares?.[this.indice] ?? null;
  }

  mostrarEstado(/** @type {string | undefined} */ texto) {
    this.refs.estado.textContent = texto ?? '';
  }

  aoTeclarCampo = (/** @type {KeyboardEvent} */ evento) => {
    if (evento.key === 'Enter') {
      evento.preventDefault(); // nunca submete o formulário de compra
      this.pesquisar();
    } else if (evento.key === 'ArrowDown' && !this.refs.resultados.hidden) {
      evento.preventDefault();
      this.refs.lista.querySelector('button')?.focus();
    } else if (evento.key === 'Escape' && !this.refs.resultados.hidden) {
      this.esconderResultados();
    }
  };

  aoEditarCampo = () => {
    this.cancelarPesquisa();
    // Mudar o texto depois de escolher apaga o lugar.
    if (this.lugar) this.host.aplicar({ tipo: 'lugar', indice: this.indice, lugar: null });
  };

  consultaAtual() {
    return this.refs.campo.value.trim().replace(/\s+/g, ' ');
  }

  cancelarPesquisa() {
    if (!this.pedidos?.cancelar()) return;
    this.terminarOcupado();
    this.mostrarEstado('');
  }

  terminarOcupado() {
    this.refs.campo.removeAttribute('aria-busy');
    this.el.classList.remove('is-a-procurar');
  }

  pesquisar = async () => {
    const { campo } = this.refs;
    if (this.consultaAtual().length < 2) {
      this.cancelarPesquisa();
      this.mostrarEstado(this.textos.curto);
      campo.focus();
      return;
    }
    const modulo = await this.host.carregarPesquisa();
    if (!modulo) {
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    this.pedidos ??= new modulo.PesquisaLugares();
    const consulta = this.consultaAtual();
    if (consulta.length < 2) return;
    const lingua = this.host.lingua;
    if (this.pedidos.emCurso(modulo.chavePesquisa(lingua, consulta))) return;
    this.cancelarPesquisa();
    const resultado = await this.pedidos.procurar(consulta, lingua, {
      aoPedir: () => {
        this.esconderResultados();
        campo.setAttribute('aria-busy', 'true');
        this.el.classList.add('is-a-procurar');
        this.mostrarEstado(this.textos.aProcurar);
      },
      aoTerminar: () => this.terminarOcupado(),
      valida: () => this.consultaAtual() === consulta,
    });
    if (!resultado) return;
    if ('erro' in resultado) {
      this.esconderResultados();
      this.mostrarEstado(this.textos.erroRede);
      return;
    }
    this.mostrarResultados(resultado.lista);
  };

  /** @param {any[]} lista */
  mostrarResultados(lista) {
    this.resultados = lista;
    const { resultados, lista: ul } = this.refs;
    if (!lista.length) {
      ul.replaceChildren();
      resultados.hidden = true;
      this.mostrarEstado(this.textos.semResultados);
      return;
    }
    this.host.pesquisa.renderizarResultados(ul, lista, 'nnadd-conf');
    resultados.hidden = false;
    // No telemóvel a lista pode nascer por baixo da barra de compra: traz-se à vista (scroll-margin).
    resultados.scrollIntoView?.({ block: 'nearest' });
    this.mostrarEstado(preencher(this.textos.nResultados, { n: lista.length }));
  }

  esconderResultados() {
    this.refs.resultados.hidden = true;
    this.refs.lista.replaceChildren();
  }

  aoClicarResultado = (/** @type {MouseEvent} */ evento) => {
    const item = this.host.pesquisa?.lugarDoClique(evento, this.resultados);
    if (item) this.escolher(item);
  };

  aoTeclarLista = (/** @type {KeyboardEvent} */ evento) => {
    this.host.pesquisa?.teclarLista(evento, this.refs.lista, this.refs.campo, () => this.esconderResultados());
  };

  /** @param {{ lat: number, lng: number, local: string }} item */
  escolher(item) {
    this.cancelarPesquisa();
    this.refs.campo.value = item.local; // sem evento `input`: não apaga a escolha
    this.esconderResultados();
    this.host.aplicar({ tipo: 'lugar', indice: this.indice, lugar: item });
    this.refs.campo.focus();
    const l = this.lugar;
    if (l) {
      this.mostrarEstado(preencher(this.textos.escolhido, { l: l.local, c: this.host.formatarDMS(l.lat, l.lng).texto }));
      if (this.mapa && this.pin) {
        this.pin.setLatLng([l.lat, l.lng]);
        this.mapa.setView([l.lat, l.lng], ZOOM_INICIAL);
      }
    }
  }

  /** Mostra o lugar do estado (ou esconde as coordenadas). */
  renderizar() {
    const l = this.lugar;
    const r = this.refs;
    if (!l) {
      r.coordenadas.hidden = true;
      this.fecharMapa();
      return;
    }
    const { latitude, longitude } = this.host.formatarDMS(l.lat, l.lng);
    r.local.textContent = l.local;
    r.latitude.textContent = latitude;
    r.longitude.textContent = longitude;
    r.coordenadas.hidden = false;
    // Um lugar reposto do rascunho volta a aparecer no campo.
    if (!r.campo.value && document.activeElement !== r.campo) r.campo.value = l.local;
  }

  /* ----- mini-mapa ----- */

  alternarMapa = () => {
    if (this.refs.painelMapa.hidden) this.abrirMapa();
    else this.fecharMapa();
  };

  abrirMapa() {
    if (!this.lugar) return;
    const { painelMapa, abrirMapa, mapaErro } = this.refs;
    painelMapa.hidden = false;
    abrirMapa.setAttribute('aria-expanded', 'true');
    abrirMapa.textContent = this.textos.fecharMapa ?? abrirMapa.textContent;
    mapaErro.hidden = true;
    painelMapa.scrollIntoView?.({ block: 'nearest' });
    carregarLeaflet(this.host.dataset.leaflet)
      .then((L) => {
        if (!this.host.isConnected || painelMapa.hidden || !this.lugar) return;
        this.criarMapa(L);
      })
      .catch(() => {
        mapaErro.textContent = this.textos.mapaErro ?? '';
        mapaErro.hidden = false;
      });
  }

  fecharMapa() {
    const { painelMapa, abrirMapa } = this.refs;
    if (painelMapa.hidden) return;
    this.definirAjuste(false);
    painelMapa.hidden = true;
    abrirMapa.setAttribute('aria-expanded', 'false');
    abrirMapa.textContent = this.textos.ajustarMapa ?? abrirMapa.textContent;
  }

  /** @param {any} L */
  criarMapa(L) {
    const { lat, lng } = /** @type {{ lat: number, lng: number }} */ (this.lugar);
    if (this.mapa) {
      this.mapa.invalidateSize();
      this.pin.setLatLng([lat, lng]);
      this.mapa.setView([lat, lng], this.mapa.getZoom() || ZOOM_INICIAL);
      return;
    }
    // Fechado por omissão, para o dedo que passa por cima continuar a fazer scroll.
    const mapa = L.map(this.refs.mapa, {
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      keyboard: false,
      tap: false,
      boxZoom: false,
    });
    mapa.attributionControl.setPrefix(false);
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(mapa);
    mapa.setView([lat, lng], ZOOM_INICIAL);
    const icone = L.divIcon({ className: 'nnadd-conf__pin', iconSize: [24, 24], iconAnchor: [12, 12] });
    const pin = L.marker([lat, lng], { icon: icone, draggable: false, keyboard: false, autoPan: true }).addTo(mapa);
    pin.on('dragend', () => this.aoMoverPin(pin.getLatLng()));
    mapa.on('click', (/** @type {any} */ e) => {
      if (!this.aAjustar) return;
      pin.setLatLng(e.latlng);
      this.aoMoverPin(e.latlng);
    });
    this.mapa = mapa;
    this.pin = pin;
  }

  /** @param {boolean} ligado */
  definirAjuste(ligado) {
    const { ajustar, concluido } = this.refs;
    this.aAjustar = ligado && Boolean(this.mapa);
    if (this.mapa) {
      for (const h of [this.mapa.dragging, this.mapa.touchZoom, this.pin?.dragging]) {
        if (!h) continue;
        if (this.aAjustar) h.enable();
        else h.disable();
      }
    }
    this.refs.mapa.classList.toggle('is-a-ajustar', this.aAjustar);
    const tinhaFoco = document.activeElement === ajustar || document.activeElement === concluido;
    ajustar.hidden = this.aAjustar;
    concluido.hidden = !this.aAjustar;
    if (tinhaFoco) (this.aAjustar ? concluido : ajustar).focus();
  }

  /**
   * A longitude fora de [-180, 180) (o mapa dá a volta ao mundo) é
   * normalizada pelo estado, em `lerLugar`.
   * @param {{ lat: number, lng: number }} latlng
   */
  aoMoverPin(latlng) {
    const l = this.lugar;
    if (!l) return;
    this.host.aplicar({ tipo: 'lugar', indice: this.indice, lugar: { lat: latlng.lat, lng: latlng.lng, local: l.local } });
  }

  aoTeclarMapa = (/** @type {KeyboardEvent} */ evento) => {
    if (!this.mapa || !this.pin) return;
    const passo = evento.shiftKey ? PASSO_PX_GRANDE : PASSO_PX;
    /** @type {Record<string, [number, number]>} */
    const direcoes = { ArrowUp: [0, -passo], ArrowDown: [0, passo], ArrowLeft: [-passo, 0], ArrowRight: [passo, 0] };
    const delta = direcoes[evento.key];
    if (!delta) return;
    evento.preventDefault();
    const ponto = this.mapa.latLngToContainerPoint(this.pin.getLatLng());
    const novo = this.mapa.containerPointToLatLng([ponto.x + delta[0], ponto.y + delta[1]]);
    this.pin.setLatLng(novo);
    if (!this.mapa.getBounds().pad(-0.1).contains(novo)) this.mapa.panTo(novo);
    this.aoMoverPin(novo);
  };

  desligar() {
    this.mapa?.remove();
    this.mapa = null;
    this.pin = null;
    this.aAjustar = false;
    this.fecharMapa();
  }
}

/* ---------------------------------------------------------------------------
 * O elemento.
 * ------------------------------------------------------------------------- */

class NnaddConfiguradorForm extends HTMLElement {
  /** @type {Record<string, any>} */ textos = {};
  /** @type {Record<string, any>} */ refs = {};
  /** @type {LugarUI[]} */ lugares = [];
  /** @type {any} */ conf = null;
  /** @type {any} */ desenho = null;
  /** @type {any} */ glifos = null;
  /** @type {any} */ modEstado = null;
  /** @type {any} */ maquina = null;
  /** @type {any} */ estado = null;
  /** @type {(lat: number, lng: number) => { latitude: string, longitude: string, texto: string }} */
  formatarDMS = () => ({ latitude: '', longitude: '', texto: '' });
  /** @type {any} */ pesquisa = null;
  /** @type {Promise<any> | null} */ #pesquisaPromise = null;
  /** @type {Promise<any> | null} */ #carregadorPromise = null;
  /** @type {{ opcoes: { peca: string, cor: string, tamanho: string }, variante: string, variantes: Record<string, (string | null)[]> }} */
  produto = { opcoes: { peca: '', cor: '', tamanho: '' }, variante: '', variantes: {} };
  falhou = false;
  /** Se a peça ainda é a de exemplo (ver `rascunhoDeExemplo` em assets/nnadd-configurador-estado.js). */
  exemplo = false;
  /** @type {AbortController | null} */ #ouvintes = null;
  #iniciado = false;
  /** O último rascunho escrito no `sessionStorage` (texto JSON). */
  /** @type {string | null} */ #ultimoRascunho = null;
  /** As últimas propriedades escritas nas entradas escondidas (texto JSON). */
  /** @type {string | null} */ #ultimasPropriedades = null;
  /** A chave de tudo o que a pré-visualização lê, da última vez que se desenhou. */
  /** @type {string | null} */ #ultimaPrevia = null;
  /**
   * O último desenho com os mapas todos, e a peça, a cor, a tinta e a disposição dele: ao mudar o mapa
   * ou o lugar, fica (esbatido) até o novo estar pronto.
   * @type {{ frente: string, costas: string | null, forma: string } | null}
   */
  #ultimoCompleto = null;
  /** A foto ao vivo: tabelas, URLs e o último desenho de cada lado (ver `ligarFoto`). */
  /** @type {{ areas?: any, topoPol?: number, areasCostas?: any, topoCostasPol?: Record<string, number>, larguraCostasPol?: Record<string, number>, imagens?: Record<string, string>, mediaId?: string, lados: { frente: string, costas: string | null } | null, real: boolean } | null} */ #foto = null;
  /** @type {MutationObserver | null} */ #observadorFoto = null;
  /**
   * O lado que a foto ao vivo mostra. Vive aqui, e não na galeria, para sobreviver ao morph do tema
   * e às mudanças de cor, peça e tamanho (R10).
   * @type {'frente' | 'costas'}
   */
  #lado = 'frente';
  /** "Virar" à espera da foto de costas (o botão fica ocupado). */
  #aPedirCostas = false;
  /** A última foto de costas pedida falhou: a foto fica na frente e o botão volta a tentar. */
  #erroCostas = false;
  /** As fotos de costas pedidas, por URL (R11: só ao virar, uma vez cada). */
  /** @type {Map<string, Promise<boolean>>} */ #costasPedidas = new Map();
  /** @type {Set<string>} */ #costasProntas = new Set();
  /** A peça e a cor da foto de costas na camada; fica até a da cor nova chegar. */
  /** @type {{ peca: string, cor: string } | null} */ #costasMostradas = null;
  /** A foto de costas da cor nova que se espera, já virado (ver `trocarCostas`). */
  /** @type {string | null} */ #costasATrocar = null;
  /** A disposição da última vez que se acertou o lado (ver `sincronizarLado`). */
  /** @type {string | null} */ #disposicaoFoto = null;
  /**
   * As variantes do produto lidas uma vez (ver `lerVariantes`): id → `{ peca, costas, cor, tamanho }`,
   * como devolve `valoresVariante`. null até os módulos chegarem.
   * @type {Map<string, { peca: string | null | undefined, costas: boolean, cor: string | null, tamanho: unknown }> | null}
   */
  #variantes = null;
  /** As peças com variante "+ costas" (ver `temVarianteCostas`). */
  /** @type {Set<string>} */ #pecasComCostas = new Set();
  /** O pai da figura da pré-visualização, para a devolver quando o diálogo fecha. */
  /** @type {HTMLElement | null} */ #previaPai = null;
  /** O elemento com foco antes de o diálogo abrir. */
  /** @type {Element | null} */ #focoAntes = null;
  /** A barra de compra: o que está à vista (botões do tema, rodapé) e o observador. */
  /** @type {IntersectionObserver | null} */ #observadorBarra = null;
  /** @type {Set<Element>} */ #aVistaDaBarra = new Set();
  /** @type {ResizeObserver | null} */ #observadorMedidas = null;
  /** @type {number} */ #adicionadaTimer = 0;
  /** Só telemóvel: a faixa e a barra existem até 749 px (como o CSS). */
  #telemovel = typeof matchMedia === 'function' ? matchMedia('(max-width: 749px)') : null;

  get lingua() {
    return this.dataset.lingua || document.documentElement.lang || 'en';
  }

  connectedCallback() {
    this.#ouvintes?.abort();
    this.#ouvintes = new AbortController();
    document.addEventListener('shopify:product:select', this.aoMudarVariante, { signal: this.#ouvintes.signal });
    document.addEventListener(EVENTO_CARRINHO, this.aoAtualizarCarrinho, { signal: this.#ouvintes.signal });
    window.visualViewport?.addEventListener('resize', this.aoRedimensionar, { signal: this.#ouvintes.signal });
    // Ao passar a computador com o diálogo aberto, fecha-se: a figura volta ao fluxo.
    this.#telemovel?.addEventListener('change', this.aoMudarLargura, { signal: this.#ouvintes.signal });
    if (this.#iniciado) return;
    this.#iniciado = true;

    for (const el of this.querySelectorAll('[data-ref]')) {
      if (el.closest('[data-lugar]')) continue;
      this.refs[/** @type {HTMLElement} */ (el).dataset.ref ?? ''] = el;
    }
    try {
      this.textos = JSON.parse(this.querySelector('script[data-textos]')?.textContent ?? '{}');
    } catch {
      this.textos = {};
    }
    try {
      const dados = JSON.parse(this.querySelector('script[data-produto]')?.textContent ?? '{}');
      this.produto = {
        opcoes: { peca: '', cor: '', tamanho: '', ...(dados?.opcoes ?? {}) },
        variante: idVariante(dados?.variante),
        variantes: dados?.variantes && typeof dados.variantes === 'object' ? dados.variantes : {},
      };
    } catch {
      // Sem dados do produto: a compra fica bloqueada em 'variante'.
    }
    this.lugares = [...this.querySelectorAll('[data-lugar]')].map(
      (el, i) => new LugarUI(this, /** @type {HTMLElement} */ (el), i === 1 ? 1 : 0)
    );

    // A validade passa a ser gerida aqui; até os módulos chegarem, o campo continua inválido.
    const campo = this.lugares[0]?.refs.campo;
    campo?.removeAttribute('pattern');
    campo?.removeAttribute('required');
    campo?.setCustomValidity(this.textos.erroCarregar || ' ');

    // A pesquisa carrega-se na primeira vez que é precisa (`pesquisar` e
    // `aplicarLugarDoUrl` esperam por `carregarPesquisa()`).
    this.iniciar().catch((erro) => {
      console.error('[nnadd-configurador] Os módulos não carregaram:', erro);
      this.falhou = true;
      campo?.setCustomValidity(this.textos.erroCarregar || ' ');
      this.refs.bloqueio.textContent = this.textos.erroCarregar ?? '';
    });
  }

  disconnectedCallback() {
    this.#ouvintes?.abort();
    this.#ouvintes = null;
    this.#observadorFoto?.disconnect();
    this.#observadorFoto = null;
    this.#observadorBarra?.disconnect();
    this.#observadorBarra = null;
    this.#observadorMedidas?.disconnect();
    this.#observadorMedidas = null;
    clearTimeout(this.#adicionadaTimer);
    for (const l of this.lugares) l.desligar();
  }

  async iniciar() {
    const d = this.dataset;
    // Os ficheiros do mapa começam já, em paralelo com os outros (quase sempre há mapa: o exemplo leva).
    this.carregador().catch(() => {});
    const [conf, glifos, mapas, coordinates, modEstado] = await Promise.all(
      [d.desenho, d.glifos, d.mapas, d.coordinates, d.estado].map((url) => {
        if (!url) throw new Error('Falta um URL de módulo');
        return import(url);
      })
    );
    this.conf = conf;
    this.glifos = glifos;
    this.modEstado = modEstado;
    this.formatarDMS = coordinates.formatarDMS;
    // O logo oficial vem do mesmo módulo da Coordinates (R12: vai sempre no peito).
    this.desenho = conf.criarDesenho({ mapas, glifos, formatarDMS: coordinates.formatarDMS, logotipo: coordinates.LOGOTIPO });
    this.maquina = modEstado.criarEstado({ conf, desenho: this.desenho, glifos, mapas, formatarDMS: coordinates.formatarDMS, lingua: this.lingua });
    this.lerVariantes();

    const valores = this.valoresVariante(this.varianteAtual()) ?? this.valoresVariante(this.produto.variante);
    this.estado = this.maquina.inicial(valores ?? {});
    if (valores) this.estado = this.maquina.aplicar(this.estado, { tipo: 'variante', ...valores }).estado;

    // O rascunho de um link "copiar link" ou, sem ele, o da troca de língua (KTD15).
    /** @type {any} */
    let rascunho = this.rascunhoDoLink();
    try {
      if (!rascunho) {
        const texto = sessionStorage.getItem(this.chaveRascunho());
        if (texto) rascunho = JSON.parse(texto);
      }
    } catch {
      // sessionStorage indisponível ou rascunho estragado: começa do zero.
      rascunho = null;
    }
    // Sem peça nenhuma, o exemplo. A marca de exemplo vive na sessão, para a troca de língua a manter.
    if (!rascunho && !/[?&]lugar=/.test(window.location.search)) {
      rascunho = this.modEstado.rascunhoDeExemplo();
      this.marcarExemplo(rascunho.lugares[0]);
    } else if (rascunho) {
      // A marca só vale se o rascunho ainda tiver a terra do exemplo (a secção da página inicial
      // escreve outro rascunho por cima, sem saber da marca).
      const l = rascunho.lugares?.[0];
      this.exemplo = Boolean(l) && this.lerMarcaExemplo() === `${l.lat},${l.lng}`;
    }
    if (rascunho) this.estado = this.maquina.restaurar(this.estado, rascunho).estado;
    // A disposição do rascunho que a variante do tema ainda não tem (a troca de língua perde o
    // `?variant=`, e a página abre na variante sem costas): aplica-se e pede-se a variante ao tema,
    // como quando o cliente a escolhe (KTD5: a variante paga manda, mas é o tema que a vai dar).
    const pedirVariante = this.reporDisposicaoDoRascunho(rascunho);
    // Escreve-se já, para a troca de língua não sortear outra frase (KTD10).
    this.guardarRascunho();

    this.construirControlos();
    this.ligarControlos();
    this.ligarFaixa();
    this.ligarBarra();
    this.ligarPartilha();
    if (this.lugares[0]?.refs.exemplo) this.lugares[0].refs.exemplo.hidden = !this.exemplo;
    // "Mais opções" abre quando o rascunho já traz alguma coisa lá dentro.
    if (this.refs.mais) this.refs.mais.open = Boolean(this.estado.qr || this.estado.historia || this.estado.publicar);
    this.setAttribute('data-pronto', '');
    // A foto ao vivo é um extra: se a tabela não chegar, a galeria fica como está.
    this.ligarFoto().catch((erro) => console.warn('[nnadd-configurador] Sem foto ao vivo:', erro));
    this.renderizar();
    if (pedirVariante) this.sincronizarVariante();
    this.talvezCarregarMapa();
    this.aplicarLugarDoUrl();
  }

  /**
   * O rascunho de um link "copiar link" (`?design=`), que ganha ao da sessão. Sai do URL depois de
   * lido: a partir daí manda o rascunho da sessão, e recarregar ou trocar de língua não desfaz o que
   * o cliente mudou.
   */
  rascunhoDoLink() {
    const url = new URL(window.location.href);
    const texto = url.searchParams.get(this.modEstado.PARAM_PARTILHA);
    if (texto === null) return null;
    // `exemplo=1`: o link vem dos exemplos da página inicial (sections/nnadd-exemplos.liquid), e a
    // peça mostra o aviso de exemplo até o cliente mudar a terra.
    const deExemplo = url.searchParams.get('exemplo') === '1';
    url.searchParams.delete(this.modEstado.PARAM_PARTILHA);
    url.searchParams.delete('exemplo');
    try {
      history.replaceState(history.state, '', url);
    } catch {
      // Sem history: o parâmetro fica, e recarregar volta à peça do link.
    }
    const rascunho = this.modEstado.lerPartilha(texto);
    const terra = deExemplo ? rascunho?.lugares?.[0] : null;
    this.marcarExemplo(terra && typeof terra.lat === 'number' && typeof terra.lng === 'number' ? terra : null);
    return rascunho;
  }

  /** A terra do exemplo que a sessão guardou (`lat,lng`), ou null. */
  lerMarcaExemplo() {
    try {
      return sessionStorage.getItem(`${this.chaveRascunho()}:exemplo`);
    } catch {
      return null;
    }
  }

  /**
   * Marca a peça como o exemplo, com a terra dele (guardada na sessão, para a troca de língua manter o
   * aviso), ou desmarca-a com `null`; mostra ou esconde o aviso.
   */
  marcarExemplo(/** @type {{ lat: number, lng: number } | null} */ terra) {
    const sim = Boolean(terra);
    this.exemplo = sim;
    try {
      if (terra) sessionStorage.setItem(`${this.chaveRascunho()}:exemplo`, `${terra.lat},${terra.lng}`);
      else sessionStorage.removeItem(`${this.chaveRascunho()}:exemplo`);
    } catch {
      // Sem sessionStorage: a troca de língua perde o aviso, não o exemplo.
    }
    const aviso = this.lugares?.[0]?.refs.exemplo;
    if (aviso) aviso.hidden = !sim;
  }

  /** O URL desta página com a variante e a peça (`?design=`), para quem o abre ver a mesma peça. */
  urlDaPeca() {
    const url = new URL(window.location.pathname, window.location.origin);
    const variante = this.varianteAtual();
    if (variante) url.searchParams.set('variant', variante);
    url.searchParams.set(this.modEstado.PARAM_PARTILHA, this.modEstado.codificarPartilha(this.maquina.rascunho(this.estado)));
    return url.href;
  }

  /** "Copiar link": copia `urlDaPeca()`; sem acesso à área de transferência, mostra o link selecionado. */
  ligarPartilha() {
    const { copiarLink, linkEstado, linkUrl } = this.refs;
    if (!copiarLink) return;
    copiarLink.addEventListener('click', async () => {
      const url = this.urlDaPeca();
      try {
        await navigator.clipboard.writeText(url);
        linkUrl.hidden = true;
        linkEstado.textContent = copiarLink.dataset.copiado ?? '';
      } catch {
        linkEstado.textContent = copiarLink.dataset.manual ?? '';
        linkUrl.value = url;
        linkUrl.hidden = false;
        linkUrl.focus();
        linkUrl.select();
      }
    });
  }

  /** Uma escolha nova torna o link copiado antigo: apaga o aviso e o link à vista. */
  limparPartilha() {
    const { linkEstado, linkUrl } = this.refs;
    if (!linkEstado || (!linkEstado.textContent && linkUrl.hidden)) return;
    linkEstado.textContent = '';
    linkUrl.hidden = true;
  }

  /**
   * Aplica a disposição de um rascunho `v: 2` que `restaurar` deixou de fora por a variante do tema não
   * bater (e a composição e o estilo das costas que dependem dela). Diz se é preciso pedir a variante
   * ao tema. Sem variante "+ costas" para a peça, fica como está.
   * @param {any} rascunho
   */
  reporDisposicaoDoRascunho(rascunho) {
    const disposicao = rascunho?.v === 2 ? rascunho.disposicao : null;
    const atual = this.conf.DISPOSICOES.includes(this.estado.disposicao) ? this.estado.disposicao : 'frente';
    if (!this.conf.DISPOSICOES.includes(disposicao) || disposicao === atual) return false;
    if (disposicao !== 'frente' && !this.temVarianteCostas(this.estado.peca)) return false;
    const acoes = [{ tipo: 'disposicao', disposicao }];
    if (typeof rascunho.composicao === 'string') acoes.push({ tipo: 'composicao', composicao: rascunho.composicao });
    if (typeof rascunho.estiloCostas === 'string') acoes.push({ tipo: 'estiloCostas', estilo: rascunho.estiloCostas });
    for (const a of acoes) this.estado = this.maquina.aplicar(this.estado, a).estado;
    return true;
  }

  /* ----- foto ao vivo ----- */

  /**
   * A primeira foto da galeria do tema (`data-foto-media`) passa a mostrar a peça e a cor
   * escolhidas sem estampado, com o desenho do lado visível por cima onde o Printful o estampa:
   * centrado, a `topoPol` (frente) ou a `TOPO_COSTAS_POL` da peça (costas) do topo da área de
   * impressão e com a largura real (a área de cada foto, em %, vem de
   * assets/nnadd-configurador-fotos.js). "Virar" roda a foto entre frente e costas. O tema refaz a
   * galeria a cada variante (morph) e tira a camada; um MutationObserver volta a pô-la, no lado
   * que estava.
   */
  async ligarFoto() {
    const d = this.dataset;
    if (!d.fotos || !d.fotoMedia) return;
    const imagens = JSON.parse(this.querySelector('script[data-fotos-imagens]')?.textContent ?? '{}');
    const { AREAS, TOPO_POL, AREAS_COSTAS, TOPO_COSTAS_POL, LARGURA_COSTAS_POL } = await import(d.fotos);
    if (!this.isConnected) return;
    this.#foto = {
      areas: AREAS,
      topoPol: TOPO_POL,
      areasCostas: AREAS_COSTAS,
      topoCostasPol: TOPO_COSTAS_POL,
      larguraCostasPol: LARGURA_COSTAS_POL,
      imagens,
      mediaId: d.fotoMedia,
      lados: this.#foto?.lados ?? null,
      real: this.#foto?.real ?? false,
    };
    const raiz = this.closest('.shopify-section') ?? document.body;
    let pedido = 0;
    this.#observadorFoto = new MutationObserver(() => {
      if (pedido) return;
      pedido = requestAnimationFrame(() => {
        pedido = 0;
        this.aplicarFoto();
      });
    });
    this.#observadorFoto.observe(raiz, { childList: true, subtree: true });
    this.sincronizarLado();
    this.aplicarFoto();
  }

  /** Guarda o desenho dos dois lados para a foto ao vivo e redesenha-a. */
  atualizarFoto(
    /** @type {{ frente: string, costas: string | null }} */ lados,
    /** @type {boolean} */ real,
    /** @type {{ espera: boolean, anterior: boolean, legenda: string }} */ espera = { espera: false, anterior: false, legenda: '' }
  ) {
    if (this.#foto) Object.assign(this.#foto, { lados, real, ...espera });
    else this.#foto = { lados, real, ...espera };
    this.aplicarFoto();
  }

  /**
   * As classes da espera pelo mapa num contentor do desenho: `is-a-carregar` (a forma do mapa pulsa),
   * `is-anterior` (o desenho anterior, esbatido) e, quando o mapa chega, `is-a-entrar` por um
   * instante (o fade de entrada; tirado logo, para a frase escrita tecla a tecla não piscar).
   */
  alternarEspera(/** @type {HTMLElement} */ el, /** @type {boolean} */ espera, /** @type {boolean} */ anterior) {
    const vinhaDeEspera = el.classList.contains('is-a-carregar');
    el.classList.toggle('is-a-carregar', espera);
    el.classList.toggle('is-anterior', espera && anterior);
    if (vinhaDeEspera && !espera) {
      el.classList.add('is-a-entrar');
      setTimeout(() => el.classList.remove('is-a-entrar'), 400);
    }
  }

  /** A chave de uma foto em `data-fotos-imagens`: hoodie-military-green(-costas). */
  urlFoto(/** @type {string} */ peca, /** @type {string} */ cor, lado = 'frente') {
    const chave = `${peca}-${String(cor).toLowerCase().replace(/[^a-z0-9]+/g, '-')}${lado === 'costas' ? '-costas' : ''}`;
    return this.#foto?.imagens?.[chave] ?? null;
  }

  /**
   * Pede a foto de costas uma vez por URL (R11) e diz se chegou. Uma falha esquece o pedido, para
   * "Tentar de novo" voltar a pedir.
   */
  pedirFotoCostas(/** @type {string} */ url) {
    let pedido = this.#costasPedidas.get(url);
    if (!pedido) {
      pedido = new Promise((resolver) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolver(true);
        img.onerror = () => resolver(false);
        img.src = url;
      }).then((ok) => {
        if (ok) this.#costasProntas.add(url);
        else this.#costasPedidas.delete(url);
        return ok;
      });
      this.#costasPedidas.set(url, pedido);
    }
    return pedido;
  }

  /**
   * Acerta o lado quando a disposição muda: "só frente" volta à frente (e esconde "Virar"), "só
   * costas" abre virada; as outras mantêm o lado.
   */
  sincronizarLado() {
    const e = this.estado;
    if (!this.#foto?.imagens || !e || e.disposicao === this.#disposicaoFoto) return;
    this.#disposicaoFoto = e.disposicao;
    if (!this.maquina.temCostas(e)) {
      // Voltar a "só frente" vira a foto: anuncia-se, como quando o cliente carrega em "Virar".
      if (this.#lado === 'costas') this.anunciarLado(this.textos.foto?.frente);
      this.#lado = 'frente';
      this.#erroCostas = false;
    } else if (e.disposicao === 'costas' && this.#lado !== 'costas') {
      void this.mostrarLado('costas');
    }
  }

  anunciarLado(/** @type {string | undefined} */ texto) {
    if (this.refs.fotoLado) this.refs.fotoLado.textContent = texto ?? '';
  }

  /** "Virar": para as costas, pede a foto antes de rodar; se falhar, fica na frente. */
  async mostrarLado(/** @type {'frente' | 'costas'} */ alvo) {
    const e = this.estado;
    if (!this.#foto?.imagens || !e) return;
    const t = this.textos.foto ?? {};
    if (alvo === 'costas') {
      if (!this.maquina.temCostas(e) || this.#aPedirCostas) return;
      const url = this.urlFoto(e.peca, e.cor, 'costas');
      if (!url) return;
      this.#aPedirCostas = true;
      this.#erroCostas = false;
      this.aplicarFoto();
      const ok = await this.pedirFotoCostas(url);
      this.#aPedirCostas = false;
      // Entretanto passou a "só frente": fica na frente, e uma falha deste pedido já não conta.
      if (!this.maquina.temCostas(this.estado)) {
        this.aplicarFoto();
        return;
      }
      if (!ok) {
        this.falharCostas();
        return;
      }
    } else {
      this.#erroCostas = false;
    }
    this.#lado = alvo;
    this.anunciarLado(t[alvo]);
    this.aplicarFoto();
  }

  /** Muda a cor já virado: a foto de costas anterior fica até a da cor nova chegar. */
  trocarCostas(/** @type {string} */ url) {
    if (this.#costasATrocar === url) return;
    this.#costasATrocar = url;
    this.pedirFotoCostas(url).then((ok) => {
      this.#costasATrocar = null;
      const e = this.estado;
      if (ok) return this.aplicarFoto();
      if (this.#lado !== 'costas' || this.urlFoto(e.peca, e.cor, 'costas') !== url) return;
      this.#lado = 'frente';
      this.falharCostas();
    });
  }

  /** O texto de "Virar" e do anúncio quando a foto de costas falha. */
  textoErroCostas() {
    const t = this.textos.foto ?? {};
    return `${t.erroCostas ?? ''} ${t.tentarDeNovo ?? ''}`.trim();
  }

  /** A foto de costas falhou: a foto fica na frente, com o erro, e "Virar" volta a tentar. */
  falharCostas() {
    this.#erroCostas = true;
    this.anunciarLado(this.textoErroCostas());
    this.aplicarFoto();
  }

  /**
   * O estilo (posição em % da foto) do desenho `svg` numa área: centrado, a `topoPol` do topo da
   * área e com a largura real (a viewBox em mm).
   */
  estiloDesenho(/** @type {string} */ svg, /** @type {any} */ area, /** @type {number} */ larguraPol, /** @type {number} */ topoPol) {
    const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
    if (!vb || !area || !larguraPol) return null;
    const pctPorPol = area.width / larguraPol;
    const largura = (Number(vb[1]) / 25.4) * pctPorPol;
    return `left:${n(area.left + (area.width - largura) / 2)}%;top:${n(area.top + topoPol * pctPorPol)}%;width:${n(largura)}%`;
  }

  /** Uma face da foto: a peça e o desenho do lado. */
  pintarFace(/** @type {HTMLElement} */ face, /** @type {string} */ url, /** @type {string} */ svg, /** @type {string | null} */ estilo, /** @type {boolean} */ real) {
    const img = /** @type {HTMLImageElement} */ (face.querySelector('img'));
    if (img.getAttribute('src') !== url) img.setAttribute('src', url);
    const desenho = /** @type {HTMLElement} */ (face.querySelector('.nnadd-conf-foto__desenho'));
    if (desenho.dataset.svg !== svg) {
      desenho.innerHTML = svg;
      desenho.dataset.svg = svg;
    }
    if (estilo) desenho.setAttribute('style', estilo);
    desenho.hidden = !svg || !estilo;
    desenho.classList.toggle('is-marcador', !real);
    this.alternarEspera(desenho, Boolean(this.#foto?.espera), Boolean(this.#foto?.anterior));
  }

  aplicarFoto() {
    const f = this.#foto;
    const e = this.estado;
    if (!f?.areas || !f.lados || !e?.peca || !e.cor) return;
    const url = this.urlFoto(e.peca, e.cor);
    const larguraPol = this.conf?.PECAS?.[e.peca]?.area?.larguraPol;
    const estiloFrente = this.estiloDesenho(f.lados.frente, f.areas[e.peca]?.[e.cor], larguraPol, f.topoPol ?? 0);
    if (!url || !estiloFrente) return;

    const comCostas = this.maquina.temCostas(e);
    if (!comCostas && this.#lado === 'costas') {
      this.#lado = 'frente';
      this.anunciarLado(this.textos.foto?.frente);
    }
    const lado = this.#lado;
    // As costas: a da cor atual se já chegou; senão a anterior fica e pede-se a nova (só virado).
    const urlCostas = comCostas ? this.urlFoto(e.peca, e.cor, 'costas') : null;
    if (urlCostas && this.#costasProntas.has(urlCostas)) this.#costasMostradas = { peca: e.peca, cor: e.cor };
    else if (urlCostas && lado === 'costas') this.trocarCostas(urlCostas);
    const mostradas = this.#costasMostradas;
    const urlMostradas = mostradas && this.urlFoto(mostradas.peca, mostradas.cor, 'costas');
    const svgCostas = f.lados.costas ?? '';
    const estiloCostas =
      mostradas && svgCostas
        ? this.estiloDesenho(svgCostas, f.areasCostas?.[mostradas.peca]?.[mostradas.cor], f.larguraCostasPol?.[mostradas.peca] ?? 0, f.topoCostasPol?.[mostradas.peca] ?? 0)
        : null;

    const t = this.textos.foto ?? {};
    const rotulo = this.#erroCostas
      ? this.textoErroCostas()
      : lado === 'costas'
        ? t.verFrente ?? ''
        : t.verCostas ?? '';

    const raiz = this.closest('.shopify-section') ?? document;
    for (const media of raiz.querySelectorAll(`.product-media[data-media-id="${CSS.escape(f.mediaId ?? '')}"]`)) {
      let camada = /** @type {HTMLElement | null} */ (media.querySelector(':scope > .nnadd-conf-foto'));
      if (!camada) {
        camada = document.createElement('div');
        camada.className = 'nnadd-conf-foto';
        camada.setAttribute('aria-hidden', 'true');
        camada.innerHTML =
          '<div class="nnadd-conf-foto__cartao"><div class="nnadd-conf-foto__face" data-lado="frente">' +
          '<img class="nnadd-conf-foto__peca" alt="" decoding="async"><div class="nnadd-conf-foto__desenho"></div></div></div>' +
          '<p class="nnadd-conf-foto__estado" hidden></p>';
        media.append(camada);
      }
      // "A desenhar o mapa de …" por cima da foto (a camada é aria-hidden: o estado da pré-visualização é que se anuncia).
      const estado = /** @type {HTMLElement | null} */ (camada.querySelector('.nnadd-conf-foto__estado'));
      if (estado) {
        if (estado.textContent !== (f.legenda ?? '')) estado.textContent = f.legenda ?? '';
        estado.hidden = !f.legenda;
      }
      const cartao = /** @type {HTMLElement} */ (camada.querySelector('.nnadd-conf-foto__cartao'));
      this.pintarFace(/** @type {HTMLElement} */ (cartao.querySelector('[data-lado="frente"]')), url, f.lados.frente, estiloFrente, f.real);

      // A face das costas só entra no DOM depois de a primeira foto de costas chegar (R11).
      let costas = /** @type {HTMLElement | null} */ (cartao.querySelector('[data-lado="costas"]'));
      if (!costas && urlMostradas) {
        costas = document.createElement('div');
        costas.className = 'nnadd-conf-foto__face';
        costas.dataset.lado = 'costas';
        costas.innerHTML = '<img class="nnadd-conf-foto__peca" alt="" decoding="async"><div class="nnadd-conf-foto__desenho"></div>';
        cartao.append(costas);
      }
      if (costas && urlMostradas) this.pintarFace(costas, urlMostradas, svgCostas, estiloCostas, f.real);
      cartao.classList.toggle('is-costas', lado === 'costas' && !!urlMostradas);

      // "Virar" fica fora da camada (que não recebe cliques), num canto da foto.
      let virar = /** @type {HTMLButtonElement | null} */ (media.querySelector(':scope > .nnadd-conf-foto__virar'));
      if (!virar) {
        virar = document.createElement('button');
        virar.type = 'button';
        virar.className = 'nnadd-conf-foto__virar';
        // Sem chegar à galeria: o clique na foto abre o zoom do tema e o arrastar muda de foto.
        virar.addEventListener('pointerdown', (ev) => ev.stopPropagation());
        virar.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          void this.mostrarLado(this.#lado === 'costas' ? 'frente' : 'costas');
        });
        media.append(virar);
      }
      virar.hidden = !comCostas;
      virar.disabled = this.#aPedirCostas;
      if (this.#aPedirCostas) virar.setAttribute('aria-busy', 'true');
      else virar.removeAttribute('aria-busy');
      virar.classList.toggle('is-erro', this.#erroCostas);
      // Só quando muda: o texto novo é uma mutação que o MutationObserver veria.
      if (virar.textContent !== rotulo) virar.textContent = rotulo;
    }
  }

  chaveRascunho() {
    return `${RASCUNHO_PREFIXO}${this.dataset.produtoId ?? ''}`;
  }

  /* ----- pesquisa ----- */

  carregarPesquisa() {
    if (!this.#pesquisaPromise) {
      const url = this.dataset.pesquisa;
      this.#pesquisaPromise = (url ? import(url) : Promise.reject(new Error('Falta data-pesquisa')))
        .then((m) => {
          this.pesquisa = m;
          return m;
        })
        .catch(() => {
          this.#pesquisaPromise = null;
          return null;
        });
    }
    return this.#pesquisaPromise;
  }

  /** `?lugar=…&lat=…&lng=…` (da secção da página inicial) escolhe o primeiro lugar, se ainda não houver. */
  async aplicarLugarDoUrl() {
    if (!/[?&]lugar=/.test(window.location.search)) return;
    const m = await this.carregarPesquisa();
    const lugar = m?.lugarDoUrl(window.location.search);
    if (!lugar || !this.maquina || this.estado.lugares[0]) return;
    this.lugares[0].refs.campo.value = lugar.local;
    this.aplicar({ tipo: 'lugar', indice: 0, lugar });
  }

  /* ----- estado ----- */

  /**
   * Aplica uma ação ao estado e atualiza a página. Uma escolha do cliente
   * substitui os avisos anteriores; as respostas assíncronas (variante do
   * tema, mapa) só os mudam se trouxerem avisos novos.
   * @param {any} acao
   * @param {boolean} [doCliente]
   */
  aplicar(acao, doCliente = !ACOES_DO_SISTEMA.has(acao.tipo)) {
    if (!this.maquina) return;
    const r = this.maquina.aplicar(this.estado, acao);
    this.estado = r.estado;
    if (r.avisos.length || doCliente) this.mostrarAvisos(r.avisos);
    if (doCliente) this.limparPartilha();
    // Mudar a terra do exemplo (ou tirá-la) faz a peça passar a ser do cliente.
    if (this.exemplo && doCliente && acao.tipo === 'lugar' && (acao.indice ?? 0) === 0) this.marcarExemplo(null);
    this.renderizar();
    this.guardarRascunho();
    this.talvezCarregarMapa();
  }

  /** Escreve o rascunho só quando mudou (a frase muda a cada tecla, o mapa não). */
  guardarRascunho() {
    try {
      const texto = JSON.stringify(this.maquina.rascunho(this.estado));
      if (texto === this.#ultimoRascunho) return;
      sessionStorage.setItem(this.chaveRascunho(), texto);
      this.#ultimoRascunho = texto;
    } catch {
      // Sem sessionStorage: a troca de língua começa do zero.
    }
  }

  /** O carregador de dados de mapa (U4), importado só quando é preciso. */
  carregador() {
    if (!this.#carregadorPromise) {
      const d = this.dataset;
      this.#carregadorPromise = (async () => {
        if (!d.mapasDados) throw new Error('Falta data-mapas-dados');
        const m = await import(d.mapasDados);
        const modulos = await m.importarModulos({ tiles: d.mapasTiles, vectorTile: d.vendorVectorTile, png: d.vendorPng });
        return m.criarCarregador(modulos);
      })().catch((erro) => {
        this.#carregadorPromise = null;
        throw erro;
      });
    }
    return this.#carregadorPromise;
  }

  /**
   * Carrega o primeiro mapa que falta. Em frente + costas há dois (um por
   * lado): marcar este como a carregar passa por `aplicar`, que volta aqui e
   * pede o seguinte.
   */
  async talvezCarregarMapa() {
    const p = this.maquina?.pedidosCarga(this.estado)[0];
    if (!p) return;
    this.aplicar({ tipo: 'mapa_a_carregar', chave: p.chave });
    /** @type {any} */
    let carga;
    try {
      const c = await this.carregador();
      carga = await c.carregar(p.pedido);
    } catch (erro) {
      console.warn('[nnadd-configurador] O mapa não carregou:', erro);
      carga = { ok: false, motivo: 'rede' };
    }
    if (!this.isConnected) return;
    this.aplicar({ tipo: 'mapa_carregado', chave: p.chave, carga });
  }

  /* ----- variante ----- */

  /** O campo `id` (a variante) do formulário de compra, ou null. */
  campoIdDoForm() {
    const form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
    return form instanceof HTMLFormElement ? form.elements.namedItem('id') : null;
  }

  varianteAtual() {
    const campoId = this.campoIdDoForm();
    const valor = campoId instanceof HTMLInputElement || campoId instanceof HTMLSelectElement ? campoId.value : '';
    return idVariante(valor) || this.produto.variante;
  }

  /**
   * As chaves de peça, cor e tamanho de uma variante e se é "+ costas", ou
   * null se não for conhecida. Sem opção Peça no produto, `peca` fica
   * undefined (a peça não muda) e `costas` false.
   * @param {string} id
   */
  valoresVariante(id) {
    return this.#variantes?.get(id) ?? null;
  }

  /**
   * Lê as variantes do produto uma vez, depois de os módulos chegarem (as variantes não mudam na
   * página): `#variantes` para `valoresVariante` e `#pecasComCostas` para `temVarianteCostas`.
   */
  lerVariantes() {
    const { lerOpcaoPeca, resolverCor } = this.modEstado;
    const variantes = this.produto.variantes;
    const lidas = new Map();
    for (const id of Object.keys(variantes)) {
      const v = variantes[id];
      if (!v) continue;
      const opcao = lerOpcaoPeca(v[0], this.maquina.pecas);
      if (opcao?.costas) this.#pecasComCostas.add(opcao.peca);
      const peca = this.produto.opcoes.peca ? opcao : undefined;
      const preco = Number(v[3]);
      lidas.set(
        id,
        Object.freeze({
          peca: peca === undefined ? undefined : (peca?.peca ?? null),
          costas: peca?.costas === true,
          cor: resolverCor(v[1], this.conf.ETIQUETAS_CORES),
          tamanho: v[2],
          // Em cêntimos da moeda servida; null quando o Liquid não o trouxe.
          preco: Number.isFinite(preco) ? preco : null,
        })
      );
    }
    this.#variantes = lidas;
  }

  /**
   * O id da variante que o estado pede (peça com ou sem costas pela disposição, cor e tamanho), ou
   * null se o produto não a tiver.
   */
  idVarianteAlvo() {
    const { resolverTamanho } = this.modEstado;
    const e = this.estado;
    const { costas } = this.maquina.varianteAlvo(e);
    return (
      [...(this.#variantes ?? [])].find(
        ([, v]) => (v.peca === undefined || (v.peca === e.peca && v.costas === costas)) && v.cor === e.cor && resolverTamanho(v.tamanho, this.conf.TAMANHOS) === e.tamanho
      )?.[0] ?? null
    );
  }

  /**
   * O preço a mostrar na faixa e na barra, formatado na língua: o da variante que a disposição pede,
   * sem esperar que o tema a selecione; se o produto não a tiver, o da variante atual. '' sem preço.
   */
  precoAtual() {
    const id = this.idVarianteAlvo() ?? this.varianteAtual();
    const centimos = this.#variantes?.get(id)?.preco;
    return typeof centimos === 'number' ? this.formatarMoeda(centimos) : '';
  }

  /** O seletor de variantes do tema nesta secção (não o dos cartões nem dos diálogos). */
  seletor() {
    const raiz = this.closest('.shopify-section') ?? document;
    return [...raiz.querySelectorAll('variant-picker')].find((p) => !p.closest('product-card, dialog, quick-add-dialog')) ?? null;
  }

  /**
   * Leva o seletor do tema à peça, cor e tamanho do estado. O tema trata do
   * resto (preço, galeria, `id`) e responde com `shopify:product:select`. Sem
   * seletor, muda o `id` do formulário diretamente.
   */
  sincronizarVariante() {
    const { lerOpcaoPeca, resolverCor, resolverTamanho } = this.modEstado;
    const e = this.estado;
    // A peça e se é "+ costas" (a disposição escolhe a variante, KTD5).
    const { costas } = this.maquina.varianteAlvo(e);
    const chavePeca = (/** @type {{ peca: string, costas: boolean } | null} */ r) => (r ? `${r.peca}${r.costas ? '+costas' : ''}` : null);
    const alvos = [
      { nome: this.produto.opcoes.peca, alvo: chavePeca({ peca: e.peca, costas }), resolver: (/** @type {string} */ v) => chavePeca(lerOpcaoPeca(v, this.maquina.pecas)) },
      { nome: this.produto.opcoes.cor, alvo: e.cor, resolver: (/** @type {string} */ v) => resolverCor(v, this.conf.ETIQUETAS_CORES) },
      { nome: this.produto.opcoes.tamanho, alvo: e.tamanho, resolver: (/** @type {string} */ v) => resolverTamanho(v, this.conf.TAMANHOS) },
    ].filter((a) => a.nome && a.alvo);

    const seletor = this.seletor();
    if (seletor) {
      /** @type {HTMLInputElement[]} */
      const radios = [];
      /** @type {HTMLSelectElement | null} */
      let select = null;
      for (const a of alvos) {
        const entradas = /** @type {HTMLInputElement[]} */ ([...seletor.querySelectorAll('input[type="radio"]')]).filter(
          (i) => i.dataset.optionName === a.nome
        );
        const certa = entradas.find((i) => a.resolver(i.value) === a.alvo);
        if (certa && !certa.checked) radios.push(certa);
        for (const s of /** @type {HTMLSelectElement[]} */ ([...seletor.querySelectorAll('select')])) {
          const opcao = [...s.options].find((o) => o.dataset.optionName === a.nome && a.resolver(o.value) === a.alvo);
          if (opcao && !opcao.selected) {
            s.value = opcao.value;
            select = s;
          }
        }
      }
      if (radios.length) {
        // As outras opções ficam marcadas antes do clique: o tema lê todas as marcadas.
        for (const r of radios.slice(0, -1)) r.checked = true;
        radios[radios.length - 1].click();
      } else if (select) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const id = this.idVarianteAlvo();
    const campoId = this.campoIdDoForm();
    if (id && campoId instanceof HTMLInputElement) campoId.value = id;
  }

  /**
   * Troca de variante no seletor do tema (como em assets/nnadd-coordinates.js).
   * @param {Event} evento `shopify:product:select`
   */
  aoMudarVariante = (evento) => {
    const origem = evento.target instanceof Element ? evento.target : null;
    if (!origem || origem.closest('product-card, quick-add-dialog, dialog') || !this.maquina) return;
    const seccao = this.closest('.shopify-section');
    if (seccao && !seccao.contains(origem)) return;
    const e = /** @type {any} */ (evento);
    const aplicarId = (/** @type {string} */ id) => {
      const v = id ? this.valoresVariante(id) : null;
      if (v) this.aplicar({ tipo: 'variante', ...v });
    };
    aplicarId(idVariante(e.detail?.variantId));
    Promise.resolve(e.promise)
      .then((/** @type {any} */ r) => {
        if (this.isConnected) aplicarId(idVariante(r?.detail?.resource?.id ?? r?.variant?.id));
      })
      .catch(() => {
        // Pedido cancelado por outra troca.
      });
  };

  aoRedimensionar = () => {
    // Teclado aberto no telemóvel: a faixa e a barra escondem-se, para o campo ter ecrã (KTD15).
    const vv = window.visualViewport;
    if (!vv) return;
    this.classList.toggle('is-teclado', window.innerHeight - vv.height > 150);
  };

  aoMudarLargura = () => {
    if (!this.#telemovel?.matches && this.refs.dialogo?.open) this.refs.dialogo.close();
  };

  /* ----- faixa e diálogo da pré-visualização (telemóvel) ----- */

  ligarFaixa() {
    const { faixa, dialogo, fecharDialogo } = this.refs;
    if (!faixa || !dialogo || typeof dialogo.showModal !== 'function') return;
    faixa.addEventListener('click', () => this.abrirPrevia());
    fecharDialogo.addEventListener('click', () => dialogo.close());
    // Um toque fora do conteúdo (no fundo) fecha.
    dialogo.addEventListener('click', (ev) => {
      if (ev.target === dialogo) dialogo.close();
    });
    dialogo.addEventListener('close', this.aoFecharPrevia);
    // As alturas da faixa e da barra, para os campos nunca ficarem por baixo delas (scroll-margin).
    if ('ResizeObserver' in window) {
      this.#observadorMedidas = new ResizeObserver(() => this.medirFaixaEBarra());
      this.#observadorMedidas.observe(faixa);
      if (this.refs.barra) this.#observadorMedidas.observe(this.refs.barra);
    }
  }

  medirFaixaEBarra() {
    const { faixa, barra } = this.refs;
    this.style.setProperty('--nnadd-conf-faixa-altura', `${faixa?.offsetHeight ?? 0}px`);
    this.style.setProperty('--nnadd-conf-barra-altura', `${barra && !barra.hidden ? barra.offsetHeight : 0}px`);
  }

  /** A figura passa para o diálogo e o diálogo abre em modal (foco preso, Esc fecha). */
  abrirPrevia() {
    const { dialogo, dialogoCorpo, previa } = this.refs;
    if (!dialogo || dialogo.open) return;
    this.#previaPai = previa.parentElement;
    this.#focoAntes = document.activeElement;
    dialogoCorpo.append(previa);
    document.documentElement.classList.add('nnadd-conf-dialogo-aberto');
    dialogo.showModal();
    dialogo.scrollTop = 0;
  }

  /** O diálogo fechou (botão, Esc ou toque fora): a figura volta ao sítio e o foco a quem o tinha. */
  aoFecharPrevia = () => {
    const { previa, faixa } = this.refs;
    if (this.#previaPai && previa.parentElement !== this.#previaPai) this.#previaPai.prepend(previa);
    document.documentElement.classList.remove('nnadd-conf-dialogo-aberto');
    const foco = this.#focoAntes instanceof HTMLElement && this.#focoAntes.isConnected ? this.#focoAntes : faixa;
    this.#focoAntes = null;
    foco?.focus?.();
  };

  /**
   * Os recortes da faixa: o desenho de cada lado que o leva, com o nome do lado. Em só frente, a
   * frente; quando o desenho todo vai atrás, as costas; em frente + costas, os dois.
   * @param {{ frente: string, costas: string | null }} lados
   * @param {boolean} real
   */
  renderizarFaixa(lados, real) {
    const { faixa, faixaRecortes } = this.refs;
    if (!faixa || !faixaRecortes) return;
    const e = this.estado;
    const m = this.maquina;
    const corPeca = m.pecas[e.peca]?.cores?.[e.cor];
    const visiveis = !m.temCostas(e) ? ['frente'] : m.fraseNasCostas(e) ? ['costas'] : ['frente', 'costas'];
    const nomes = this.textos.lados ?? {};
    faixaRecortes.innerHTML = visiveis
      .filter((lado) => lados[lado])
      .map(
        (lado) =>
          `<span class="nnadd-conf__recorte">${svgDoRecorte(/** @type {string} */ (lados[lado]), corPeca?.hex, corPeca?.tom)}` +
          `<span class="nnadd-conf__recorte-lado">${escaparXml(nomes[lado] ?? lado)}</span></span>`
      )
      .join('');
    faixa.classList.toggle('tem-desenho', real);
  }

  /* ----- barra de compra (telemóvel) ----- */

  /** O formulário de compra do tema. */
  formDoTema() {
    const form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  /** O botão "Adicionar ao carrinho" do tema, dentro do formulário de compra. */
  botaoDoTema() {
    return /** @type {HTMLButtonElement | null} */ (this.formDoTema()?.querySelector('add-to-cart-component button[type="submit"]') ?? null);
  }

  /**
   * A barra: o botão carrega no do tema (a mesma validação nativa e o mesmo pedido); esconde-se
   * enquanto os botões do tema ou o rodapé estão à vista, para não os tapar.
   */
  ligarBarra() {
    const { barra, barraBotao } = this.refs;
    if (!barra) return;
    barraBotao.addEventListener('click', () => this.botaoDoTema()?.click());
    const alvos = [this.formDoTema()?.closest('.buy-buttons-block'), document.querySelector('footer, [class*="footer-group"]')].filter(Boolean);
    if (!('IntersectionObserver' in window) || !alvos.length) {
      barra.hidden = false;
      return;
    }
    this.#observadorBarra = new IntersectionObserver((entradas) => {
      for (const en of entradas) {
        if (en.isIntersecting) this.#aVistaDaBarra.add(en.target);
        else this.#aVistaDaBarra.delete(en.target);
      }
      barra.hidden = this.#aVistaDaBarra.size > 0;
      this.medirFaixaEBarra();
    });
    for (const a of alvos) this.#observadorBarra.observe(/** @type {Element} */ (a));
  }

  /** O preço na faixa e na barra, a razão do bloqueio e o estado do botão, a cada renderização. */
  renderizarBarra() {
    const r = this.refs;
    const preco = this.precoAtual();
    if (r.faixaPreco && r.faixaPreco.textContent !== preco) r.faixaPreco.textContent = preco;
    if (!r.barra) return;
    if (r.barraPreco.textContent !== preco) r.barraPreco.textContent = preco;
    const estado = r.bloqueio.textContent ?? '';
    if (r.barraEstado.textContent !== estado) r.barraEstado.textContent = estado;
    r.barraEstado.classList.toggle('is-pronto', r.bloqueio.classList.contains('is-pronto'));
    const botao = this.botaoDoTema();
    r.barraBotao.disabled = !botao || botao.disabled;
  }

  /**
   * Uma linha entrou no carrinho a partir deste formulário: o botão da barra diz "Adicionada" e a
   * gaveta do carrinho do tema abre (a definição "abrir a gaveta" da loja está desligada; aqui o
   * cliente precisa de ver que a peça entrou).
   * @param {Event & { action?: string, promise?: Promise<any> }} evento
   */
  aoAtualizarCarrinho = (evento) => {
    const origem = evento.target instanceof Element ? evento.target : null;
    if (!origem || evento.action !== 'add') return;
    const seccao = this.closest('.shopify-section');
    if (seccao && !seccao.contains(origem)) return;
    if (origem.closest('product-card, quick-add-dialog, dialog')) return;
    Promise.resolve(evento.promise)
      .then((r) => {
        if (!this.isConnected || r?.detail?.didError) return;
        this.marcarAdicionada();
        this.abrirGaveta();
      })
      .catch(() => {
        // Pedido cancelado ou falhado: o tema mostra o erro no botão.
      });
  };

  marcarAdicionada() {
    const { barraBotao } = this.refs;
    if (!barraBotao) return;
    clearTimeout(this.#adicionadaTimer);
    barraBotao.textContent = barraBotao.dataset.adicionada || barraBotao.textContent;
    barraBotao.classList.add('is-adicionada');
    this.#adicionadaTimer = window.setTimeout(() => {
      barraBotao.textContent = barraBotao.dataset.adicionar || '';
      barraBotao.classList.remove('is-adicionada');
    }, ADICIONADA_MS);
  }

  /** Abre a gaveta do carrinho do tema (`<theme-drawer>` à volta de `cart-drawer-component`), se existir e estiver fechada. */
  abrirGaveta() {
    const gaveta = /** @type {any} */ (document.querySelector('cart-drawer-component')?.closest('theme-drawer') ?? null);
    if (gaveta && !gaveta.isOpen && typeof gaveta.open === 'function') gaveta.open();
  }

  /* ----- controlos ----- */

  /** Os nomes na língua da página. */
  etiqueta(/** @type {Record<string, string> | undefined} */ etiquetas) {
    return this.modEstado.etiquetaNaLingua(etiquetas, this.lingua);
  }

  /** Um nome dentro de uma frase: em minúsculas, menos em alemão. */
  noTexto(/** @type {string} */ nome) {
    return this.lingua.startsWith('de') ? nome : nome.toLocaleLowerCase(this.lingua);
  }

  /**
   * Um rádio com rótulo e, se vier, uma amostra de cor (a cor nunca é o único sinal: o nome vai sempre).
   * @param {string} nome
   * @param {string} valor
   * @param {string} texto
   * @param {string | null} [hex]
   * @param {string | null} [fundo]
   */
  radio(nome, valor, texto, hex = null, fundo = null) {
    const rotulo = document.createElement('label');
    rotulo.className = 'nnadd-conf__opcao';
    const entrada = document.createElement('input');
    entrada.type = 'radio';
    entrada.name = nome;
    entrada.value = valor;
    entrada.className = 'nnadd-conf__radio';
    rotulo.append(entrada);
    if (hex) {
      const amostra = document.createElement('span');
      amostra.className = 'nnadd-conf__amostra';
      amostra.setAttribute('aria-hidden', 'true');
      amostra.style.setProperty('--nnadd-conf-amostra', hex);
      if (fundo) amostra.style.setProperty('--nnadd-conf-amostra-fundo', fundo);
      rotulo.append(amostra);
    }
    const t = document.createElement('span');
    t.textContent = texto;
    rotulo.append(t);
    return rotulo;
  }

  construirControlos() {
    const { pecas, tamanhos, grupoPeca, grupoTamanho } = this.refs;
    const id = this.id || `nnadd-conf-${Math.random().toString(36).slice(2, 8)}`;
    this.dataset.uid = id;
    pecas.replaceChildren(
      ...this.maquina.pecasVisiveis().map((/** @type {string} */ p) => this.radio(`${id}-peca`, p, this.etiqueta(this.maquina.pecas[p].etiquetas)))
    );
    grupoPeca.hidden = false;
    tamanhos.replaceChildren(...this.conf.TAMANHOS.map((/** @type {string} */ t) => this.radio(`${id}-tamanho`, t, t)));
    grupoTamanho.hidden = !this.produto.opcoes.tamanho;
    // O extra das costas no formato da língua (o Liquid deixa-o no da loja).
    const extra = this.extraCostas();
    for (const el of this.refs.disposicoes.querySelectorAll('[data-extra]')) {
      if (extra === null) /** @type {HTMLElement} */ (el).hidden = true;
      else if (extra) el.textContent = extra;
    }
  }

  /**
   * O extra das disposições com costas (R12), formatado na língua da página:
   * `+10 €`, `+€10`. '' se não se conseguir formatar (fica o texto do Liquid);
   * null sem extra (zero ou em falta), para não mostrar "+0".
   */
  extraCostas() {
    const centimos = Number(this.dataset.extraCostas);
    if (!Number.isFinite(centimos) || centimos <= 0) return null;
    return this.formatarMoeda(centimos, true);
  }

  /**
   * Cêntimos da moeda servida (`data-moeda`) no formato da língua, sem casas decimais quando são
   * zero (`45 €`, `€45`); com `sinal`, sempre com sinal (`+10 €`). '' se não se conseguir formatar.
   * @param {number} centimos @param {boolean} [sinal]
   */
  formatarMoeda(centimos, sinal = false) {
    const valor = centimos / 100;
    const casas = Number.isInteger(valor) ? 0 : 2;
    try {
      return new Intl.NumberFormat(this.lingua, {
        style: 'currency',
        currency: this.dataset.moeda || 'EUR',
        signDisplay: sinal ? 'always' : 'auto',
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      }).format(valor);
    } catch {
      return '';
    }
  }

  /** Se o produto tem a variante "+ costas" da peça (sem ela, as disposições com costas não se compram). */
  temVarianteCostas(/** @type {string} */ peca) {
    return Boolean(this.produto.opcoes.peca) && this.#pecasComCostas.has(peca);
  }

  /** As cores da peça atual (recria quando a peça muda). */
  renderizarCores() {
    const { cores, grupoCor } = this.refs;
    const peca = this.maquina.pecas[this.estado.peca];
    const chave = `${this.estado.peca}`;
    if (cores.dataset.peca !== chave) {
      const tinhaFoco = cores.contains(document.activeElement);
      cores.replaceChildren(
        ...Object.entries(peca?.cores ?? {}).map(([c, v]) =>
          this.radio(`${this.dataset.uid}-cor`, c, this.etiqueta(this.conf.ETIQUETAS_CORES[c]) || c, /** @type {any} */ (v).hex)
        )
      );
      cores.dataset.peca = chave;
      if (tinhaFoco) cores.querySelector('input:checked, input')?.focus();
    }
    grupoCor.hidden = !this.produto.opcoes.cor;
  }

  /** As tintas permitidas na cor da peça (recria quando a cor muda). */
  renderizarTintas() {
    const { tintas, grupoTinta } = this.refs;
    const e = this.estado;
    const permitidas = e.cor ? this.conf.tintasPermitidas(e.peca, e.cor, this.maquina.pecas) : [];
    const fundo = e.cor ? this.maquina.pecas[e.peca]?.cores?.[e.cor]?.hex ?? null : null;
    const chave = `${e.peca}|${e.cor}`;
    if (tintas.dataset.cor !== chave) {
      const tinhaFoco = tintas.contains(document.activeElement);
      tintas.replaceChildren(
        ...permitidas.map((/** @type {string} */ t) =>
          this.radio(`${this.dataset.uid}-tinta`, t, this.etiqueta(this.conf.TINTAS[t].etiquetas), this.conf.TINTAS[t].hex, fundo)
        )
      );
      tintas.dataset.cor = chave;
      if (tinhaFoco) tintas.querySelector('input')?.focus();
    }
    grupoTinta.hidden = permitidas.length === 0;
  }

  ligarControlos() {
    const r = this.refs;
    r.pecas.addEventListener('change', (/** @type {Event} */ ev) => {
      const v = /** @type {HTMLInputElement} */ (ev.target).value;
      this.aplicar({ tipo: 'peca', peca: v });
      this.sincronizarVariante();
    });
    r.cores.addEventListener('change', (/** @type {Event} */ ev) => {
      const cor = /** @type {HTMLInputElement} */ (ev.target).value;
      const antes = this.estado;
      // As costas vêm da disposição atual: trocar a cor não muda a disposição (KTD5).
      this.aplicar({ tipo: 'variante', peca: antes.peca, cor, tamanho: antes.tamanho, costas: this.maquina.varianteAlvo(antes).costas }, true);
      this.sincronizarVariante();
    });
    r.tamanhos.addEventListener('change', (/** @type {Event} */ ev) => {
      const tamanho = /** @type {HTMLInputElement} */ (ev.target).value;
      const antes = this.estado;
      this.aplicar({ tipo: 'variante', peca: antes.peca, cor: antes.cor, tamanho, costas: this.maquina.varianteAlvo(antes).costas }, true);
      this.sincronizarVariante();
    });
    // Um grupo de rádios cuja ação tem o nome do campo: `{ tipo: 'tinta', tinta: valor }`.
    const aoMudar = (/** @type {HTMLElement} */ grupo, /** @type {string} */ tipo) => {
      grupo.addEventListener('change', (/** @type {Event} */ ev) => {
        this.aplicar({ tipo, [tipo]: /** @type {HTMLInputElement} */ (ev.target).value });
      });
    };
    aoMudar(r.tintas, 'tinta');
    aoMudar(r.composicoes, 'composicao');
    aoMudar(r.estilos, 'estilo');
    // A disposição escolhe a variante (com ou sem costas): pede-a logo ao tema (KTD5).
    r.disposicoes.addEventListener('change', (/** @type {Event} */ ev) => {
      this.aplicar({ tipo: 'disposicao', disposicao: /** @type {HTMLInputElement} */ (ev.target).value });
      this.sincronizarVariante();
    });
    r.estilosCostas.addEventListener('change', (/** @type {Event} */ ev) => {
      this.aplicar({ tipo: 'estiloCostas', estilo: /** @type {HTMLInputElement} */ (ev.target).value });
    });
    r.frase.addEventListener('input', () => this.aplicar({ tipo: 'frase', frase: r.frase.value }));
    r.qr.addEventListener('change', () => this.aplicar({ tipo: 'qr', qr: r.qr.checked }));
    r.historia.addEventListener('input', () => this.aplicar({ tipo: 'historia', historia: r.historia.value }));
    r.publicar.addEventListener('change', () => this.aplicar({ tipo: 'publicar', publicar: r.publicar.checked }));
    r.confirmar.addEventListener('change', () => this.aplicar({ tipo: 'confirmar', confirmado: r.confirmar.checked }));
  }

  /**
   * Os avisos das transições (AE1, AE3), numa região `aria-live`.
   * @param {{ campo: string, motivo: string }[]} avisos
   */
  mostrarAvisos(avisos) {
    const t = this.textos.avisos ?? {};
    const e = this.estado;
    const frases = avisos.map((a) => {
      if (a.campo === 'cor') return preencher(t.cor, { c: this.noTexto(this.etiqueta(this.conf.ETIQUETAS_CORES[e.cor]) || String(e.cor)) });
      if (a.campo === 'tinta') return preencher(t.tinta, { t: this.noTexto(this.etiqueta(this.conf.TINTAS[e.tinta]?.etiquetas)) });
      if (a.campo === 'estilo') return preencher(t.estilo, { s: this.nomeEstilo(e.estilo) });
      // Sem estilo das costas que sirva, fala o bloqueio junto ao botão.
      if (a.campo === 'estiloCostas') return e.estiloCostas ? preencher(t.estiloCostas, { s: this.nomeEstilo(e.estiloCostas) }) : '';
      if (a.campo === 'disposicao') return preencher(t.disposicao, { d: this.noTexto(this.nomeDisposicao(e.disposicao)) });
      if (a.campo === 'composicao') return t[a.motivo] ?? '';
      if (a.campo === 'confirmar') return t.mudou ?? '';
      return '';
    });
    const texto = frases.filter(Boolean).join(' ');
    this.refs.avisos.textContent = texto;
    this.refs.avisos.hidden = !texto;
  }

  nomeEstilo(/** @type {string} */ estilo) {
    return this.querySelector(`[data-rotulo="${estilo}"]`)?.textContent?.trim() ?? estilo;
  }

  nomeDisposicao(/** @type {string} */ disposicao) {
    return this.querySelector(`[data-rotulo-disposicao="${disposicao}"]`)?.textContent?.trim() ?? disposicao;
  }

  /* ----- renderização ----- */

  renderizar() {
    const e = this.estado;
    const m = this.maquina;
    if (!e || !m) return;
    const r = this.refs;
    const marcar = (/** @type {HTMLElement} */ grupo, /** @type {string | null} */ valor) => {
      for (const i of /** @type {HTMLInputElement[]} */ ([...grupo.querySelectorAll('input[type="radio"]')])) i.checked = i.value === valor;
    };

    this.renderizarCores();
    this.renderizarTintas();
    marcar(r.pecas, e.peca);
    marcar(r.cores, e.cor);
    marcar(r.tamanhos, e.tamanho);
    marcar(r.tintas, e.tinta);
    marcar(r.composicoes, e.composicao);
    marcar(r.estilos, e.estilo);
    marcar(r.disposicoes, e.disposicao);
    marcar(r.estilosCostas, e.estiloCostas);

    // Disposições: sem a variante "+ costas" da peça, as com costas ficam desativadas.
    const comCostasAVenda = this.temVarianteCostas(e.peca);
    for (const i of /** @type {HTMLInputElement[]} */ ([...r.disposicoes.querySelectorAll('input[type="radio"]')])) {
      const semVariante = i.value !== 'frente' && !comCostasAVenda;
      i.disabled = semVariante && i.value !== e.disposicao;
      i.setAttribute('aria-disabled', String(semVariante));
    }

    // Composições: frente pequena + costas é só mapa (o grupo esconde-se);
    // frente + costas leva sempre mapa à frente (sai "só coordenadas").
    r.grupoComposicao.hidden = e.disposicao === 'frente_pequena_costas';
    const soCoordenadas = /** @type {HTMLInputElement | null} */ (r.composicoes.querySelector('input[value="coordenadas"]'));
    const rotuloSo = /** @type {HTMLElement | null} */ (soCoordenadas?.closest('label') ?? null);
    if (soCoordenadas && rotuloSo) {
      rotuloSo.hidden = e.disposicao === 'frente_costas';
      soCoordenadas.disabled = rotuloSo.hidden;
    }

    // Estilos: os indisponíveis no lugar atual ficam visíveis, desativados, com a razão.
    const comMapa = m.comMapa(e.composicao);
    r.grupoEstilo.hidden = !comMapa;
    // Com dois mapas, a legenda diz qual é o da frente.
    const legenda = r.grupoEstilo.querySelector('legend');
    if (legenda) {
      legenda.dataset.original ??= legenda.textContent ?? '';
      legenda.textContent = e.disposicao === 'frente_costas' && this.textos.estiloFrente ? this.textos.estiloFrente : legenda.dataset.original;
    }
    for (const s of m.estilos(e)) {
      const entrada = /** @type {HTMLInputElement | null} */ (r.estilos.querySelector(`input[value="${s.estilo}"]`));
      const motivo = r.estilos.querySelector(`[data-motivo="${s.estilo}"]`);
      if (entrada) {
        entrada.disabled = !s.disponivel && entrada.value !== e.estilo;
        entrada.setAttribute('aria-disabled', String(!s.disponivel));
      }
      if (motivo) motivo.textContent = s.motivo ? this.textos.motivoEstilo?.[s.motivo] ?? '' : '';
    }

    // O estilo das costas, só em frente + costas (R5): o da frente não aparece
    // (AE2); os que não servem ficam desativados, com a razão.
    const costasCompletas = e.disposicao === 'frente_costas';
    r.grupoEstiloCostas.hidden = !costasCompletas;
    if (costasCompletas) {
      for (const s of m.estilosCostas(e)) {
        const entrada = /** @type {HTMLInputElement | null} */ (r.estilosCostas.querySelector(`input[value="${s.estilo}"]`));
        const motivo = r.estilosCostas.querySelector(`[data-motivo="${s.estilo}"]`);
        if (!entrada) continue;
        const rotulo = /** @type {HTMLElement | null} */ (entrada.closest('label'));
        if (rotulo) rotulo.hidden = s.motivo === 'igual';
        entrada.disabled = (!s.disponivel && entrada.value !== e.estiloCostas) || s.motivo === 'igual';
        entrada.setAttribute('aria-disabled', String(!s.disponivel));
        if (motivo) motivo.textContent = s.motivo ? this.textos.motivoEstilo?.[s.motivo] ?? '' : '';
      }
    }

    // Lugares.
    const lugar2 = this.lugares[1];
    if (lugar2) {
      const precisa = m.precisaLugar2(e);
      lugar2.el.hidden = !precisa;
      if (!precisa) lugar2.fecharMapa();
    }
    for (const l of this.lugares) l.renderizar();

    // Frase.
    const f = m.fraseInfo(e);
    if (r.frase.value !== e.frase && document.activeElement !== r.frase) r.frase.value = e.frase;
    // Onde vai a frase: nas costas quando o desenho todo vai atrás (R3, R4).
    const onde = (m.fraseNasCostas(e) ? r.fraseOnde.dataset.costas : r.fraseOnde.dataset.frente) ?? '';
    if (r.fraseOnde.textContent !== onde) r.fraseOnde.textContent = onde;
    r.contador.textContent =
      f.restantes >= 0 ? preencher(this.textos.restantes, { n: f.restantes }) : preencher(this.textos.excesso, { n: -f.restantes });
    r.contador.classList.toggle('is-excesso', f.restantes < 0);
    const erroFrase = f.valida
      ? ''
      : f.motivo === 'caracter'
        ? preencher(this.textos.bloqueios?.frase_caracter, { c: `“${f.caracter}” (${f.codigo})` })
        : preencher(this.textos.bloqueios?.frase_comprimento, { n: f.limite });
    r.fraseErro.textContent = erroFrase;
    r.fraseErro.hidden = !erroFrase;
    r.frase.setAttribute('aria-invalid', String(!f.valida));

    // QR, história, confirmação.
    const aceitaQR = m.pecas[e.peca]?.aceitaQR !== false;
    r.grupoQr.hidden = !aceitaQR;
    r.qr.checked = e.qr;
    if (r.historia.value !== e.historia && document.activeElement !== r.historia) r.historia.value = e.historia;
    r.publicar.checked = e.publicar;
    r.confirmar.checked = e.confirmado;

    this.renderizarPrevia();
    this.escreverPropriedades();
    this.atualizarValidade();
    this.renderizarBarra();
  }

  /** As entradas escondidas com as propriedades da linha (só as que têm valor; só reescritas quando mudam). */
  escreverPropriedades() {
    const props = this.maquina.propriedades(this.estado, { lingua: this.lingua });
    const form = this.dataset.form ?? '';
    const chave = JSON.stringify([form, props]);
    if (chave === this.#ultimasPropriedades) return;
    this.#ultimasPropriedades = chave;
    const caixa = this.refs.propriedades;
    caixa.replaceChildren(
      ...Object.entries(props).map(([k, v]) => {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = `properties[${k}]`;
        i.value = String(v);
        if (form) i.setAttribute('form', form);
        return i;
      })
    );
  }

  /** O único sítio que mexe na validade: a razão vai para o campo do problema ou para a confirmação. */
  atualizarValidade() {
    const r = this.refs;
    const campo1 = this.lugares[0]?.refs.campo;
    const campo2 = this.lugares[1]?.refs.campo;
    const todos = [campo1, campo2, r.frase, r.confirmar].filter(Boolean);
    for (const el of todos) el.setCustomValidity('');
    campo1?.removeAttribute('aria-invalid');
    campo2?.removeAttribute('aria-invalid');

    if (this.falhou) {
      campo1?.setCustomValidity(this.textos.erroCarregar || ' ');
      r.bloqueio.textContent = this.textos.erroCarregar ?? '';
      return;
    }
    const b = this.maquina.bloqueio(this.estado);
    const t = this.textos.bloqueios ?? {};
    let mensagem = t.pronto ?? '';
    if (b.bloqueado) {
      const f = this.maquina.fraseInfo(this.estado);
      mensagem = preencher(t[this.chaveBloqueio(b)] ?? t[b.motivo] ?? t.invalido ?? ' ', { n: f.limite, c: f.caracter ? `“${f.caracter}”` : '' });
      const alvo = { lugar: campo1, lugar2: campo2, frase: r.frase }[/** @type {string} */ (b.campo)] ?? r.confirmar;
      alvo?.setCustomValidity(mensagem || ' ');
      if (b.campo === 'lugar2') campo2?.setAttribute('aria-invalid', 'true');
      if (b.campo === 'lugar' && b.motivo !== 'falta_lugar') campo1?.setAttribute('aria-invalid', 'true');
    }
    if (r.bloqueio.textContent !== mensagem) r.bloqueio.textContent = mensagem;
    r.bloqueio.classList.toggle('is-pronto', !b.bloqueado);
  }

  /**
   * A chave da mensagem de bloqueio: os mapas das costas (`_costas`) e os das
   * disposições que exigem mapa (`_disposicao`, onde "só coordenadas" não é
   * saída) têm mensagens próprias; as outras usam o motivo.
   * @param {{ motivo: string, campo: string }} b
   */
  chaveBloqueio(b) {
    if (!String(b.motivo).startsWith('mapa_')) return b.motivo;
    if (b.campo === 'estiloCostas') return `${b.motivo}_costas`;
    if (b.campo === 'estilo' && this.maquina.exigeMapa(this.estado)) return `${b.motivo}_disposicao`;
    return b.motivo;
  }

  /* ----- pré-visualização ----- */

  /**
   * Os marcadores dos dois lados (`costas` null sem costas), com o que cada lado leva na disposição,
   * como `descritorDoLado` no desenho (U5): a frente pequena ou só o logo à frente quando a frase vai
   * atrás; atrás o desenho sem o logo, ou em frente + costas só o mapa no estilo das costas.
   * @param {string} hex
   */
  desenharMarcadores(hex) {
    const e = this.estado;
    const completo = { composicao: e.composicao, estilo: e.estilo, comFrase: true };
    if (!this.maquina.temCostas(e)) return { frente: this.desenharMarcador(hex, { ...completo, logo: true }), costas: null };
    if (this.maquina.fraseNasCostas(e)) {
      return {
        frente: this.desenharMarcadorPequeno(hex, e.disposicao === 'frente_pequena_costas'),
        costas: this.desenharMarcador(hex, { ...completo, logo: false }),
      };
    }
    return {
      frente: this.desenharMarcador(hex, { ...completo, logo: true }),
      costas: this.desenharMarcador(hex, { composicao: 'mapa', estilo: e.estiloCostas, comFrase: false, logo: false }),
    };
  }

  /**
   * O desenho de um lado antes de haver tudo: a forma do mapa e as linhas das
   * coordenadas a tracejado, a frase (`comFrase`) e o logo (`logo`) desenhados.
   * Em mm, como `desenharPeca`.
   * @param {string} hex
   * @param {{ composicao: string, estilo: string | null, comFrase: boolean, logo: boolean }} lado
   */
  desenharMarcador(hex, lado) {
    const e = { ...this.estado, composicao: lado.composicao, estilo: lado.estilo };
    const comMapa = this.maquina.comMapa(e.composicao);
    const forma = comMapa ? this.desenho.formaMapa(e.composicao, e.estilo, e.lugares.filter(Boolean)) : null;
    const partes = [];
    let y = 0;
    let largura = 120;
    const tracejado = `fill="none" stroke="${hex}" stroke-width="1.2" stroke-dasharray="4 3"`;
    if (forma) {
      const w = forma.tipo === 'circulo' ? forma.diametroMm : forma.larguraMm;
      const h = forma.tipo === 'circulo' ? forma.diametroMm : forma.alturaMm;
      largura = Math.max(largura, w);
      partes.push({ w, h, svg: (/** @type {number} */ x) =>
        forma.tipo === 'circulo'
          ? `<circle cx="${n(x + w / 2)}" cy="${n(y + h / 2)}" r="${n(w / 2 - 1)}" ${tracejado}/>`
          : `<rect x="${n(x + 1)}" y="${n(y + 1)}" width="${n(w - 2)}" height="${n(h - 2)}" ${tracejado}/>` });
    }
    const linhas = LINHAS_MARCADOR[comMapa && e.estilo === 'duas_terras' ? 'duas_terras' : e.composicao] ?? [];
    const so = e.composicao === 'coordenadas';
    // As mesmas medidas do desenho (U5): a primeira linha de base fica à altura
    // das maiúsculas, as seguintes à entrelinha.
    const M = this.conf.MEDIDAS;
    const passo = so
      ? { primeira: M.maiusculasCoordenadasSoMm, seguinte: M.entrelinhaCoordenadasSoMm }
      : { primeira: M.maiusculasLinhaMm, seguinte: M.entrelinhaLinhaMm };
    const corpoSvg = [];
    let topo = 0;
    for (const p of partes) {
      const x = (largura - p.w) / 2;
      corpoSvg.push(p.svg(x));
      topo += p.h;
    }
    linhas.forEach((w, i) => {
      topo += i === 0 ? (partes.length ? M.espacoMapaMm : 0) + passo.primeira : passo.seguinte;
      const x = (largura - w) / 2;
      corpoSvg.push(`<line x1="${n(x)}" y1="${n(topo - 2)}" x2="${n(x + w)}" y2="${n(topo - 2)}" ${tracejado}/>`);
    });
    const f = this.maquina.fraseInfo(e);
    if (lado.comFrase && f.valida && f.frase) {
      try {
        const { d, larguraMm } = this.glifos.desenharFrase(f.frase, M.maiusculasLinhaMm);
        topo += (partes.length || linhas.length ? M.espacoFraseMm : 0) + M.maiusculasLinhaMm;
        largura = Math.max(largura, larguraMm);
        corpoSvg.push(`<path transform="translate(${n((largura - larguraMm) / 2)} ${n(topo)})" d="${d}" fill="${hex}"/>`);
        topo += 1.5;
      } catch {
        // Uma frase que não se desenha não aparece no marcador.
      }
    }
    // O logo, por baixo de tudo, como na peça (R12); só à frente.
    if (lado.logo) {
      const logo = this.desenho.logotipo;
      topo += M.espacoLogoMm;
      corpoSvg.push(`<g fill="${hex}">${this.desenho.corpoLogotipo((largura - logo.larguraMm) / 2, topo)}</g>`);
      topo += logo.alturaMm;
    }
    return svgMarcador(largura, topo, corpoSvg.join(''));
  }

  /**
   * O marcador da frente pequena: as linhas das coordenadas (duas por lugar)
   * a tracejado e o logo por baixo; sem coordenadas, só o logo.
   * @param {string} hex
   * @param {boolean} comCoordenadas
   */
  desenharMarcadorPequeno(hex, comCoordenadas) {
    const M = this.conf.MEDIDAS;
    const logo = this.desenho.logotipo;
    const larguraLinha = 70;
    const nLinhas = comCoordenadas ? (this.maquina.precisaLugar2(this.estado) ? 4 : 2) : 0;
    const largura = Math.max(logo.larguraMm, nLinhas ? larguraLinha : 0);
    const tracejado = `fill="none" stroke="${hex}" stroke-width="1.2" stroke-dasharray="4 3"`;
    const corpoSvg = [];
    let topo = 0;
    for (let i = 0; i < nLinhas; i++) {
      topo += i === 0 ? M.maiusculasLinhaMm : M.entrelinhaLinhaMm;
      const x = (largura - larguraLinha) / 2;
      corpoSvg.push(`<line x1="${n(x)}" y1="${n(topo - 2)}" x2="${n(x + larguraLinha)}" y2="${n(topo - 2)}" ${tracejado}/>`);
    }
    if (nLinhas) topo += M.espacoLogoMm;
    corpoSvg.push(`<g fill="${hex}">${this.desenho.corpoLogotipo((largura - logo.larguraMm) / 2, topo)}</g>`);
    topo += logo.alturaMm;
    return svgMarcador(largura, topo, corpoSvg.join(''));
  }

  /**
   * Os SVG do desenho da escolha atual, um por lado (`costas` é null sem costas), ou o marcador nos
   * dois enquanto falta alguma coisa.
   */
  svgDoDesenho() {
    const e = this.estado;
    const m = this.maquina;
    const hex = this.conf.TINTAS[e.tinta]?.hex ?? '#888888';
    const estadoMapa = m.estadoMapa(e);
    const comMapa = m.comMapa(e.composicao);
    const faltaLugar = !e.lugares[0] || (m.precisaLugar2(e) && !e.lugares[1]);
    const marcador = () => ({ ...this.desenharMarcadores(hex), real: false, espera: false, anterior: false });
    if (faltaLugar || !e.cor || !e.tinta) return marcador();
    const d = m.descritor(e);
    // Uma frase inválida não impede a pré-visualização do resto (o erro está no campo).
    if (!m.fraseInfo(e).valida) d.frase = '';
    try {
      // O mapa que ainda não chegou desenha-se como uma forma a pulsar (`MAPA_EM_ESPERA`): as
      // coordenadas, a frase e o logo aparecem logo.
      const prontos = m.mapasDaPrevia(e);
      const E = this.conf.MAPA_EM_ESPERA;
      const lados = this.desenho.desenharLados(d, { frente: prontos.frente ?? E, costas: prontos.costas ?? E }, { pecasAtivas: Object.keys(m.pecas) });
      const espera = comMapa && (estadoMapa.estado !== 'pronto' || /data-camada="mapa-espera"/.test(lados.frente + (lados.costas ?? '')));
      const forma = [e.peca, e.cor, e.tinta, e.disposicao].join('|');
      // Ao mudar o mapa ou o lugar, o desenho anterior fica, esbatido, até o novo estar pronto.
      const anterior = this.#ultimoCompleto;
      if (espera && anterior?.forma === forma) return { frente: anterior.frente, costas: anterior.costas, real: true, espera, anterior: true };
      if (!espera) this.#ultimoCompleto = { frente: lados.frente, costas: lados.costas, forma };
      return { frente: lados.frente, costas: lados.costas, real: true, espera, anterior: false };
    } catch (erro) {
      console.warn('[nnadd-configurador] Pré-visualização com marcador:', erro);
      return marcador();
    }
  }

  /** O texto que resume a peça para quem não a vê. */
  textoResumo() {
    const s = this.maquina.resumo(this.estado, { lingua: this.lingua });
    const t = this.textos.resumo ?? {};
    const partes = [preencher(t.peca, { p: s.peca, c: this.noTexto(s.cor), t: this.noTexto(s.tinta) })];
    const costas = s.estiloCostas ? ` (${this.nomeEstilo(s.estiloCostas)})` : '';
    partes.push(`${this.nomeDisposicao(s.disposicao)}${costas}.`);
    const nomeComposicao = this.querySelector(`[data-rotulo="${s.composicao}"]`)?.textContent?.trim() ?? s.composicao;
    if (!s.lugares.length) partes.push(t.semLugar ?? '');
    else {
      const estilo = s.estilo ? ` (${this.nomeEstilo(s.estilo)})` : '';
      partes.push(`${nomeComposicao}${estilo}: ${s.lugares.join(' → ')}, ${s.coordenadas.join(' / ')}.`);
    }
    if (s.frase) partes.push(preencher(t.frase, { f: s.frase }));
    if (s.qr) partes.push(t.qr ?? '');
    return partes.filter(Boolean).join(' ');
  }

  renderizarPrevia() {
    const e = this.estado;
    const m = this.maquina;
    const r = this.refs;
    // Tudo o que a pré-visualização lê; sem mudanças (história, confirmação,
    // publicar), não se redesenha. Não depende do lado da foto ao vivo: com
    // costas mostra os dois lados.
    const estadoMapa = m.estadoMapa(e);
    this.sincronizarLado();
    const chave = JSON.stringify([
      e.peca, e.cor, e.tamanho, e.tinta, e.disposicao, e.composicao, e.estilo, e.estiloCostas, e.lugares, e.frase, e.qr,
      estadoMapa.estado, estadoMapa.motivo, this.lingua,
    ]);
    if (chave === this.#ultimaPrevia) return;
    this.#ultimaPrevia = chave;
    const peca = m.pecas[e.peca];
    const corPeca = peca?.cores?.[e.cor];
    const { real, frente, costas, espera, anterior } = this.svgDoDesenho();
    const lados = { frente, costas };

    const markup = svgDaPrevia({
      peca: e.peca,
      tom: corPeca?.tom,
      hex: corPeca?.hex,
      frente,
      costas: m.temCostas(e) ? costas : null,
      topoCostasPol: peca?.topoCostasPol,
      // O pormenor ampliado é o do lado com o desenho principal.
      pormenor: m.fraseNasCostas(e) ? 'costas' : 'frente',
      idTitulo: `${this.dataset.uid ?? 'nnadd-conf'}-previa-titulo`,
      titulo: this.textoResumo(),
      legendas: this.textos.lados,
    });
    const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
    r.previaSvg.replaceChildren(document.importNode(doc.documentElement, true));
    this.classList.toggle('tem-desenho', real);
    const legenda = espera ? preencher(this.textos.aCarregarMapa ?? '', { l: e.lugares[0]?.local ?? '' }) : '';
    this.atualizarFoto(lados, real, { espera, anterior, legenda });
    this.renderizarFaixa(lados, real);

    // Estado do mapa e atribuição (R21) junto da pré-visualização.
    r.previaEstado.hidden = !espera;
    r.previaEstado.textContent = legenda;
    this.alternarEspera(r.previa, espera, anterior);
    // "As duas terras" também usa dados do OpenFreeMap (a costa): leva a atribuição (R21).
    const mostraMapa = real && m.comMapa(e.composicao);
    // Os estilos nos dois lados (o das costas só em frente + costas).
    const usados = [e.estilo, ...(e.disposicao === 'frente_costas' && e.estiloCostas ? [e.estiloCostas] : [])];
    r.atribuicao.hidden = !mostraMapa;
    r.atribuicaoOfm.hidden = !usados.some((s) => ['ruas', 'edificios', 'agua', 'relevo', 'duas_terras'].includes(s));
    r.atribuicaoRelevo.hidden = !usados.includes('relevo');
  }
}

if (!customElements.get('nnadd-configurador-form')) {
  customElements.define('nnadd-configurador-form', NnaddConfiguradorForm);
}
